export const POSTHOG_PROJECT_ID = "184012";
export const POSTHOG_UI_HOST = "https://eu.posthog.com";
export const POSTHOG_CAPTURE_PROXY_HOST = "https://t.seoh.team";

export type PostHogSetupStatus =
  | "configured-live"
  | "configured-repo"
  | "partial-live"
  | "needs-live-setup"
  | "needs-code"
  | "needs-code-and-live-setup"
  | "needs-secret-gate"
  | "needs-code-and-secret-gate"
  | "needs-owner-oauth"
  | "needs-owner-decision"
  | "needs-product-decision"
  | "planned-hardware"
  | "watch-docs";

export type PostHogFeatureFlagShell = {
  readonly key: string;
  readonly status: PostHogSetupStatus;
  readonly defaultRolloutPercent: 0;
  readonly liveUrl: string;
  readonly purpose: string;
  readonly repoHook: string;
};

export const POSTHOG_FEATURE_FLAG_SHELLS = [
  {
    key: "seoh-support-widget",
    status: "configured-live",
    defaultRolloutPercent: 0,
    liveUrl: "https://eu.posthog.com/project/184012/feature_flags/198169",
    purpose: "Kill switch and rollout shell for the PostHog Support widget.",
    repoHook: "support_widget_enabled",
  },
  {
    key: "seoh-public-lead-api",
    status: "configured-live",
    defaultRolloutPercent: 0,
    liveUrl: "https://eu.posthog.com/project/184012/feature_flags/198171",
    purpose: "Server/public lead-intake rollout shell after CRM service credentials exist.",
    repoHook: "public_lead_api_enabled",
  },
  {
    key: "seoh-survey-nps",
    status: "configured-live",
    defaultRolloutPercent: 0,
    liveUrl: "https://eu.posthog.com/project/184012/feature_flags/198175",
    purpose: "Safe default-off shell for site NPS and survey program launch.",
    repoHook: "survey_nps_enabled",
  },
  {
    key: "seoh-portal-novu-nudges",
    status: "configured-live",
    defaultRolloutPercent: 0,
    liveUrl: "https://eu.posthog.com/project/184012/feature_flags/198176",
    purpose: "Portal/client nudge rollout shell for Novu-backed notifications.",
    repoHook: "portal_novu_nudges_enabled",
  },
  {
    key: "seoh-crm-analytics-batch",
    status: "configured-live",
    defaultRolloutPercent: 0,
    liveUrl: "https://eu.posthog.com/project/184012/feature_flags/198177",
    purpose: "Worker analytics-batch kill switch for PostHog endpoint reads and ERPNext aggregate writes.",
    repoHook: "crm_analytics_batch_enabled",
  },
  {
    key: "seoh-experience-registry",
    status: "configured-live",
    defaultRolloutPercent: 0,
    liveUrl: "https://eu.posthog.com/project/184012/feature_flags/198178",
    purpose: "Experience Variant Registry rollout shell for parallax, CTA, component, and gamification variants.",
    repoHook: "experience_registry_enabled",
  },
  {
    key: "seoh-posthog-workflows",
    status: "configured-live",
    defaultRolloutPercent: 0,
    liveUrl: "https://eu.posthog.com/project/184012/feature_flags/198180",
    purpose: "PostHog-native workflow shell for alerts and product-data follow-ups.",
    repoHook: "posthog_workflows_enabled",
  },
] as const satisfies readonly PostHogFeatureFlagShell[];

export type PostHogActionDefinition = {
  readonly name: string;
  readonly eventName: string;
  readonly status: PostHogSetupStatus;
  readonly nativePostHogEvent?: boolean;
  readonly ownerApprovalRef?: string;
  readonly liveSendAuthorized?: false;
  readonly purpose: string;
};

export const POSTHOG_ACTION_DEFINITIONS = [
  {
    name: "SEOH Booking CTA intent",
    eventName: "cta_book_call_click",
    status: "configured-live",
    purpose: "Booking CTA funnel and conversion intent.",
  },
  {
    name: "SEOH Contact method intent",
    eventName: "contact_method_click",
    status: "configured-live",
    purpose: "Contact method selection and channel-intent reporting.",
  },
  {
    name: "SEOH Contact email intent",
    eventName: "contact_email_click",
    status: "configured-live",
    purpose: "Email-specific contact intent.",
  },
  {
    name: "SEOH Pricing view intent",
    eventName: "pricing_band_view",
    status: "configured-live",
    purpose: "Pricing band visibility and pricing-funnel reporting.",
  },
  {
    name: "SEOH Resource engagement",
    eventName: "resource_click",
    status: "configured-live",
    purpose: "Resource and content engagement.",
  },
  {
    name: "SEOH Lead magnet request",
    eventName: "lead_magnet_request_click",
    status: "configured-live",
    purpose: "Lead magnet request intent.",
  },
  {
    name: "SEOH Locale switch intent",
    eventName: "language_switch_click",
    status: "configured-live",
    purpose: "Locale switching and i18n UX reporting.",
  },
  {
    name: "SEOH App/PWA open",
    eventName: "app_opened",
    status: "configured-live",
    purpose: "PWA/mobile-shell lifecycle reporting.",
  },
  {
    name: "SEOH Audience scope confidence",
    eventName: "scope_confidence_bumped",
    status: "configured-live",
    purpose: "Buyer-scope and gamification funnel reporting.",
  },
  {
    name: "SEOH Browser exception",
    eventName: "$exception",
    status: "configured-live",
    nativePostHogEvent: true,
    purpose: "Browser error impact, replay triage, and QA routing.",
  },
  {
    name: "SEOH Dead click friction",
    eventName: "$dead_click",
    status: "configured-live",
    nativePostHogEvent: true,
    purpose: "Dead-click friction and heatmap/replay QA.",
  },
  {
    name: "SEOH Web vitals signal",
    eventName: "$web_vitals",
    status: "configured-live",
    nativePostHogEvent: true,
    purpose: "Web Vitals monitoring and release guardrails.",
  },
  {
    name: "SEOH Portal activation step",
    eventName: "portal_activation_step",
    status: "configured-repo",
    ownerApprovalRef: "docs/retention-audit/2026-06-15-seoh-retention-owner-approved-answers.json",
    liveSendAuthorized: false,
    purpose:
      "Repo-ready activation and first-value funnel signal using retention policy v2026-06-15-owner-approved. No live send authorization.",
  },
  {
    name: "SEOH Portal adoption signal",
    eventName: "portal_adoption_signal",
    status: "configured-repo",
    ownerApprovalRef: "docs/retention-audit/2026-06-15-seoh-retention-owner-approved-answers.json",
    liveSendAuthorized: false,
    purpose: "Repo-ready portal depth, habit, and power-path signal for retention reporting. No live send authorization.",
  },
  {
    name: "SEOH Retention feedback signal",
    eventName: "retention_feedback_signal",
    status: "configured-repo",
    ownerApprovalRef: "docs/retention-audit/2026-06-15-seoh-retention-owner-approved-answers.json",
    liveSendAuthorized: false,
    purpose:
      "Repo-ready NPS, support, cancellation, praise, and referral feedback buckets without raw text. No live send authorization.",
  },
  {
    name: "SEOH Retention risk signal",
    eventName: "retention_risk_signal",
    status: "configured-repo",
    ownerApprovalRef: "docs/retention-audit/2026-06-15-seoh-retention-owner-approved-answers.json",
    liveSendAuthorized: false,
    purpose: "Repo-ready dormancy, cancellation, save, billing, and churn-risk bucket signal. No live send authorization.",
  },
  {
    name: "SEOH Retention lifecycle trigger",
    eventName: "retention_lifecycle_trigger",
    status: "configured-repo",
    ownerApprovalRef: "docs/retention-audit/2026-06-15-seoh-retention-owner-approved-answers.json",
    liveSendAuthorized: false,
    purpose:
      "Repo-ready lifecycle eligibility signal using retention policy v2026-06-15-owner-approved. It does not authorize live sends or workflows.",
  },
] as const satisfies readonly PostHogActionDefinition[];

export type PostHogCohortDefinition = {
  readonly name: string;
  readonly eventLabel: string;
  readonly status: PostHogSetupStatus;
  readonly liveUrl: string;
  readonly purpose: string;
};

export const POSTHOG_COHORT_DEFINITIONS = [
  {
    name: "SEOH Pricing intent users",
    eventLabel: "pricing_band_view",
    status: "configured-live",
    liveUrl: "https://eu.posthog.com/project/184012/cohorts/145403",
    purpose: "Pricing funnels, experiments, surveys, and CRM aggregate reporting.",
  },
  {
    name: "SEOH Booking CTA users",
    eventLabel: "cta_book_call_click",
    status: "configured-live",
    liveUrl: "https://eu.posthog.com/project/184012/cohorts/145405",
    purpose: "Booking funnels, surveys, experiments, and setup-health reporting.",
  },
  {
    name: "SEOH Contact method users",
    eventLabel: "contact_method_click",
    status: "configured-live",
    liveUrl: "https://eu.posthog.com/project/184012/cohorts/145406",
    purpose: "Contact-intent funnels and CRM aggregate reporting.",
  },
  {
    name: "SEOH Contact email users",
    eventLabel: "contact_email_click",
    status: "configured-live",
    liveUrl: "https://eu.posthog.com/project/184012/cohorts/145407",
    purpose: "Email-intent and contact-channel analysis.",
  },
  {
    name: "SEOH Resource engagement users",
    eventLabel: "resource_click",
    status: "configured-live",
    liveUrl: "https://eu.posthog.com/project/184012/cohorts/145408",
    purpose: "Content engagement, lead magnet, and campaign analysis.",
  },
  {
    name: "SEOH Lead magnet requesters",
    eventLabel: "lead_magnet_request_click",
    status: "configured-live",
    liveUrl: "https://eu.posthog.com/project/184012/cohorts/145410",
    purpose: "Lead-magnet conversion reporting and follow-up analysis.",
  },
  {
    name: "SEOH Locale switchers",
    eventLabel: "language_switch_click",
    status: "configured-live",
    liveUrl: "https://eu.posthog.com/project/184012/cohorts/145412",
    purpose: "i18n UX, locale QA, and cross-locale funnel analysis.",
  },
  {
    name: "SEOH PWA app users",
    eventLabel: "app_opened",
    status: "configured-live",
    liveUrl: "https://eu.posthog.com/project/184012/cohorts/145413",
    purpose: "App/PWA lifecycle reporting and portal nudge targeting.",
  },
  {
    name: "SEOH Audience scope users",
    eventLabel: "scope_confidence_bumped",
    status: "configured-live",
    liveUrl: "https://eu.posthog.com/project/184012/cohorts/145414",
    purpose: "Buyer-scope analysis and gamification reporting.",
  },
  {
    name: "SEOH Browser exception impacted users",
    eventLabel: "Exception",
    status: "configured-live",
    liveUrl: "https://eu.posthog.com/project/184012/cohorts/145415",
    purpose: "Error-impact replay, QA, and support workflows.",
  },
  {
    name: "SEOH Dead click friction users",
    eventLabel: "Dead click",
    status: "configured-live",
    liveUrl: "https://eu.posthog.com/project/184012/cohorts/145417",
    purpose: "Friction analysis, heatmaps, and UX QA.",
  },
  {
    name: "SEOH Web vitals signal users",
    eventLabel: "Web vitals",
    status: "configured-live",
    liveUrl: "https://eu.posthog.com/project/184012/cohorts/145418",
    purpose: "Performance monitoring and release guardrails.",
  },
] as const satisfies readonly PostHogCohortDefinition[];

export type PostHogEndpointDefinition = {
  readonly slug: string;
  readonly status: "active-materialized-verified" | "configured-repo";
  readonly zeroRowFallback?: boolean;
  readonly consumer: "crm_automation_batch" | "visitor_map" | "dashboard" | "qa";
};

export const POSTHOG_ENDPOINT_DEFINITIONS = [
  { slug: "seoh-growth-summary-30d", status: "active-materialized-verified", consumer: "dashboard" },
  { slug: "seoh-locale-surface-30d", status: "active-materialized-verified", consumer: "qa" },
  { slug: "seoh-top-routes-30d", status: "active-materialized-verified", consumer: "dashboard" },
  { slug: "seoh-visitor-map-cities-30d", status: "active-materialized-verified", consumer: "visitor_map" },
  { slug: "seoh-visitor-map-countries-30d", status: "active-materialized-verified", consumer: "visitor_map" },
  { slug: "seoh-daily-traffic-30d", status: "active-materialized-verified", consumer: "dashboard" },
  { slug: "seoh-event-volume-30d", status: "active-materialized-verified", consumer: "qa" },
  { slug: "seoh-custom-event-volume-30d", status: "active-materialized-verified", consumer: "qa" },
  { slug: "seoh-cta-performance-30d", status: "active-materialized-verified", consumer: "crm_automation_batch" },
  { slug: "seoh-contact-intent-30d", status: "active-materialized-verified", consumer: "crm_automation_batch" },
  { slug: "seoh-pricing-engagement-30d", status: "active-materialized-verified", consumer: "crm_automation_batch" },
  { slug: "seoh-language-switches-30d", status: "active-materialized-verified", consumer: "qa" },
  { slug: "seoh-acquisition-channels-30d", status: "active-materialized-verified", consumer: "dashboard" },
  { slug: "seoh-search-referrals-30d", status: "active-materialized-verified", consumer: "dashboard" },
  { slug: "seoh-campaign-performance-30d", status: "active-materialized-verified", consumer: "dashboard" },
  { slug: "seoh-device-breakdown-30d", status: "active-materialized-verified", consumer: "qa" },
  { slug: "seoh-viewport-breakdown-30d", status: "active-materialized-verified", consumer: "qa" },
  { slug: "seoh-return-visitor-breakdown-30d", status: "active-materialized-verified", consumer: "dashboard" },
  { slug: "seoh-local-time-breakdown-30d", status: "active-materialized-verified", consumer: "dashboard" },
  { slug: "seoh-resource-engagement-30d", status: "active-materialized-verified", consumer: "crm_automation_batch" },
  {
    slug: "seoh-proof-trust-engagement-30d",
    status: "active-materialized-verified",
    zeroRowFallback: true,
    consumer: "crm_automation_batch",
  },
  {
    slug: "seoh-fit-quiz-engagement-30d",
    status: "active-materialized-verified",
    zeroRowFallback: true,
    consumer: "crm_automation_batch",
  },
  {
    slug: "seoh-roi-calculator-engagement-30d",
    status: "active-materialized-verified",
    zeroRowFallback: true,
    consumer: "crm_automation_batch",
  },
  {
    slug: "seoh-partner-engagement-30d",
    status: "active-materialized-verified",
    zeroRowFallback: true,
    consumer: "crm_automation_batch",
  },
  { slug: "seoh-app-pwa-lifecycle-30d", status: "active-materialized-verified", consumer: "crm_automation_batch" },
  { slug: "seoh-technical-health-by-route-30d", status: "active-materialized-verified", consumer: "qa" },
  { slug: "seoh-web-vitals-breakdown-30d", status: "active-materialized-verified", consumer: "qa" },
  { slug: "seoh-replay-qa-health-30d", status: "active-materialized-verified", consumer: "qa" },
  { slug: "seoh-client-portal-traffic-30d", status: "active-materialized-verified", consumer: "dashboard" },
  { slug: "seoh-audience-scope-signals-30d", status: "active-materialized-verified", consumer: "crm_automation_batch" },
  { slug: "seoh-experience-variant-rollup-30d", status: "active-materialized-verified", consumer: "crm_automation_batch" },
  { slug: "seoh-component-family-rollup-30d", status: "active-materialized-verified", consumer: "crm_automation_batch" },
  { slug: "seoh-portal-activation-funnel-30d", status: "configured-repo", consumer: "dashboard" },
  { slug: "seoh-portal-adoption-signals-30d", status: "configured-repo", consumer: "dashboard" },
  { slug: "seoh-retention-feedback-loop-30d", status: "configured-repo", consumer: "crm_automation_batch" },
  { slug: "seoh-retention-lifecycle-readiness-30d", status: "configured-repo", consumer: "crm_automation_batch" },
] as const satisfies readonly PostHogEndpointDefinition[];

/**
 * Canonical (deduplicated) analytics events for aggregate rollups.
 *
 * `instrumentation-client.ts` fans a single physical observation out into
 * several event names: one impression emits `component_impression` plus, when
 * applicable, `cta_impression`, `experience_variant_exposed`,
 * `experience_zone_viewed` and `parallax_variant_viewed`; one engagement emits
 * `component_engaged` plus, when applicable, `cta_engaged`,
 * `experience_zone_engaged` and `parallax_variant_engaged`. That fan-out is
 * deliberate and QA-covered (`scripts/component-impression-browser-qa.mjs`) so
 * every surface can be reported on independently.
 *
 * The facet events are emitted *alongside* their base event, never instead of
 * it, so an aggregate must never sum a base event together with its facets:
 * `countIf(event IN ('component_engaged', 'experience_zone_engaged'))` counts a
 * single interaction twice.
 *
 * `component_impression` / `component_engaged` are the canonical bases -
 * exactly one is emitted per physical observation, and the gamification surface
 * in `components/gamification/tracking.ts` emits `component_engaged` directly -
 * so rollup impression and engagement volume derives from them alone.
 */
export const POSTHOG_CANONICAL_IMPRESSION_EVENT = "component_impression";
export const POSTHOG_CANONICAL_ENGAGEMENT_EVENT = "component_engaged";

export const POSTHOG_IMPRESSION_FANOUT_FACET_EVENTS = [
  "cta_impression",
  "experience_variant_exposed",
  "experience_zone_viewed",
  "parallax_variant_viewed",
] as const;

export const POSTHOG_ENGAGEMENT_FANOUT_FACET_EVENTS = [
  "cta_engaged",
  "experience_zone_engaged",
  "parallax_variant_engaged",
] as const;
export type PostHogEndpointQueryDefinition = {
  readonly slug: (typeof POSTHOG_ENDPOINT_DEFINITIONS)[number]["slug"];
  readonly materializationStatus: "ready-for-live-apply" | "configured-live";
  readonly description: string;
  readonly dataFreshnessSeconds: number;
  readonly isActive: true;
  readonly isMaterialized: true;
  readonly expectedColumns: readonly string[];
  readonly tags: readonly string[];
  readonly query: string;
};

export const POSTHOG_ENDPOINT_QUERY_DEFINITIONS = [
  {
    slug: "seoh-experience-variant-rollup-30d",
    materializationStatus: "configured-live",
    description:
      "Daily low-cardinality Experience Variant Registry exposure and engagement rollup for ERPNext Experience Result rows.",
    dataFreshnessSeconds: 86400,
    isActive: true,
    isMaterialized: true,
    expectedColumns: [
      "result_date",
      "source_locale",
      "experience_id",
      "component_id",
      "component_family",
      "variant_key",
      "impression_count",
      "engagement_count",
      "engagement_rate",
      "qualified_lead_assist_count",
    ],
    tags: [],
    query: `
SELECT
  toDate(timestamp) AS result_date,
  ifNull(nullIf(properties.source_locale, ''), ifNull(nullIf(properties.locale, ''), 'all')) AS source_locale,
  ifNull(nullIf(properties.experience_id, ''), 'unknown') AS experience_id,
  ifNull(nullIf(properties.component_id, ''), ifNull(nullIf(properties.cta_id, ''), 'unknown')) AS component_id,
  ifNull(nullIf(properties.component_family, ''), 'experience') AS component_family,
  ifNull(nullIf(properties.variant_key, ''), ifNull(nullIf(properties.experiment_variant, ''), 'control')) AS variant_key,
  countIf(event = 'experience_variant_exposed') AS impression_count,
  countIf(event = '${POSTHOG_CANONICAL_ENGAGEMENT_EVENT}') AS engagement_count,
  if(impression_count = 0, 0, least(round(engagement_count / impression_count, 4), 1)) AS engagement_rate,
  0 AS qualified_lead_assist_count
FROM events
WHERE timestamp >= now() - INTERVAL 30 DAY
  AND event IN (
    'experience_variant_exposed',
    'experience_zone_engaged',
    'parallax_variant_engaged',
    'cta_engaged',
    'component_engaged'
  )
GROUP BY
  result_date,
  source_locale,
  experience_id,
  component_id,
  component_family,
  variant_key
HAVING impression_count > 0 OR engagement_count > 0
ORDER BY result_date DESC, engagement_count DESC
LIMIT 500
`.trim(),
  },
  {
    slug: "seoh-component-family-rollup-30d",
    materializationStatus: "configured-live",
    description:
      "Daily low-cardinality component and CTA impression/engagement rollup for ERPNext Experience Result rows.",
    dataFreshnessSeconds: 86400,
    isActive: true,
    isMaterialized: true,
    expectedColumns: [
      "result_date",
      "source_locale",
      "experience_id",
      "component_id",
      "component_family",
      "variant_key",
      "impression_count",
      "engagement_count",
      "engagement_rate",
      "qualified_lead_assist_count",
      "completion_rate",
      "dropoff_rate",
    ],
    tags: [],
    query: `
SELECT
  toDate(timestamp) AS result_date,
  ifNull(nullIf(properties.source_locale, ''), ifNull(nullIf(properties.locale, ''), 'all')) AS source_locale,
  ifNull(nullIf(properties.experience_id, ''), 'component-rollup') AS experience_id,
  ifNull(nullIf(properties.component_id, ''), ifNull(nullIf(properties.cta_id, ''), 'unknown')) AS component_id,
  ifNull(nullIf(properties.component_family, ''), if(event LIKE 'cta_%', 'cta', 'component')) AS component_family,
  ifNull(nullIf(properties.variant_key, ''), ifNull(nullIf(properties.experiment_variant, ''), 'control')) AS variant_key,
  countIf(event = '${POSTHOG_CANONICAL_IMPRESSION_EVENT}') AS impression_count,
  countIf(event = '${POSTHOG_CANONICAL_ENGAGEMENT_EVENT}') AS engagement_count,
  if(impression_count = 0, 0, least(round(engagement_count / impression_count, 4), 1)) AS engagement_rate,
  0 AS qualified_lead_assist_count,
  if(impression_count = 0, 0, least(round(engagement_count / impression_count, 4), 1)) AS completion_rate,
  if(impression_count = 0, 0, round(1 - least(engagement_count / impression_count, 1), 4)) AS dropoff_rate
FROM events
WHERE timestamp >= now() - INTERVAL 30 DAY
  AND event IN (
    'component_impression',
    'component_engaged',
    'cta_impression',
    'cta_engaged'
  )
GROUP BY
  result_date,
  source_locale,
  experience_id,
  component_id,
  component_family,
  variant_key
HAVING impression_count > 0 OR engagement_count > 0
ORDER BY result_date DESC, engagement_count DESC
LIMIT 500
`.trim(),
  },
  {
    slug: "seoh-portal-activation-funnel-30d",
    materializationStatus: "ready-for-live-apply",
    description:
      "Daily client-portal activation-step rollup for retention first-value reporting under policy v2026-06-15-owner-approved.",
    dataFreshnessSeconds: 21600,
    isActive: true,
    isMaterialized: true,
    expectedColumns: ["result_date", "source_locale", "retention_policy_version", "activation_step", "portal_state", "event_count"],
    tags: ["seoh", "retention", "portal", "aggregate-only"],
    query: `
SELECT
  toDate(timestamp) AS result_date,
  ifNull(nullIf(properties.source_locale, ''), 'all') AS source_locale,
  ifNull(nullIf(properties.retention_policy_version, ''), '2026-06-15-owner-approved') AS retention_policy_version,
  ifNull(nullIf(properties.activation_step, ''), 'unknown') AS activation_step,
  ifNull(nullIf(properties.portal_state, ''), 'unknown') AS portal_state,
  count() AS event_count
FROM events
WHERE event = 'portal_activation_step'
  AND timestamp >= now() - INTERVAL 30 DAY
GROUP BY result_date, source_locale, retention_policy_version, activation_step, portal_state
ORDER BY result_date DESC, event_count DESC
LIMIT 500
`.trim(),
  },
  {
    slug: "seoh-portal-adoption-signals-30d",
    materializationStatus: "ready-for-live-apply",
    description: "Daily client-portal adoption signal rollup for habit, depth, and power-path reporting under policy v2026-06-15-owner-approved.",
    dataFreshnessSeconds: 21600,
    isActive: true,
    isMaterialized: true,
    expectedColumns: ["result_date", "source_locale", "retention_policy_version", "adoption_signal", "retention_stage", "signal_count_bucket", "event_count"],
    tags: ["seoh", "retention", "portal", "aggregate-only"],
    query: `
SELECT
  toDate(timestamp) AS result_date,
  ifNull(nullIf(properties.source_locale, ''), 'all') AS source_locale,
  ifNull(nullIf(properties.retention_policy_version, ''), '2026-06-15-owner-approved') AS retention_policy_version,
  ifNull(nullIf(properties.adoption_signal, ''), 'unknown') AS adoption_signal,
  ifNull(nullIf(properties.retention_stage, ''), 'unknown') AS retention_stage,
  ifNull(nullIf(properties.signal_count_bucket, ''), '0') AS signal_count_bucket,
  count() AS event_count
FROM events
WHERE event = 'portal_adoption_signal'
  AND timestamp >= now() - INTERVAL 30 DAY
GROUP BY result_date, source_locale, retention_policy_version, adoption_signal, retention_stage, signal_count_bucket
ORDER BY result_date DESC, event_count DESC
LIMIT 500
`.trim(),
  },
  {
    slug: "seoh-retention-feedback-loop-30d",
    materializationStatus: "ready-for-live-apply",
    description:
      "Daily retention feedback bucket rollup for close-loop, detractor rescue, praise, and referral readiness under policy v2026-06-15-owner-approved.",
    dataFreshnessSeconds: 21600,
    isActive: true,
    isMaterialized: true,
    expectedColumns: ["result_date", "source_locale", "retention_policy_version", "feedback_signal", "feedback_stage", "sentiment_bucket", "event_count"],
    tags: ["seoh", "retention", "feedback", "aggregate-only"],
    query: `
SELECT
  toDate(timestamp) AS result_date,
  ifNull(nullIf(properties.source_locale, ''), 'all') AS source_locale,
  ifNull(nullIf(properties.retention_policy_version, ''), '2026-06-15-owner-approved') AS retention_policy_version,
  ifNull(nullIf(properties.feedback_signal, ''), 'unknown') AS feedback_signal,
  ifNull(nullIf(properties.feedback_stage, ''), 'unknown') AS feedback_stage,
  ifNull(nullIf(properties.sentiment_bucket, ''), 'unknown') AS sentiment_bucket,
  count() AS event_count
FROM events
WHERE event = 'retention_feedback_signal'
  AND timestamp >= now() - INTERVAL 30 DAY
GROUP BY result_date, source_locale, retention_policy_version, feedback_signal, feedback_stage, sentiment_bucket
ORDER BY result_date DESC, event_count DESC
LIMIT 500
`.trim(),
  },
  {
    slug: "seoh-retention-lifecycle-readiness-30d",
    materializationStatus: "ready-for-live-apply",
    description:
      "Daily retention lifecycle eligibility rollup under policy v2026-06-15-owner-approved. This endpoint does not authorize live provider sends.",
    dataFreshnessSeconds: 21600,
    isActive: true,
    isMaterialized: true,
    expectedColumns: ["result_date", "source_locale", "retention_policy_version", "lifecycle_trigger", "delivery_channel", "retention_stage", "event_count"],
    tags: ["seoh", "retention", "lifecycle", "aggregate-only"],
    query: `
SELECT
  toDate(timestamp) AS result_date,
  ifNull(nullIf(properties.source_locale, ''), 'all') AS source_locale,
  ifNull(nullIf(properties.retention_policy_version, ''), '2026-06-15-owner-approved') AS retention_policy_version,
  ifNull(nullIf(properties.lifecycle_trigger, ''), 'unknown') AS lifecycle_trigger,
  ifNull(nullIf(properties.delivery_channel, ''), 'none') AS delivery_channel,
  ifNull(nullIf(properties.retention_stage, ''), 'unknown') AS retention_stage,
  count() AS event_count
FROM events
WHERE event = 'retention_lifecycle_trigger'
  AND timestamp >= now() - INTERVAL 30 DAY
GROUP BY result_date, source_locale, retention_policy_version, lifecycle_trigger, delivery_channel, retention_stage
ORDER BY result_date DESC, event_count DESC
LIMIT 500
`.trim(),
  },
] as const satisfies readonly PostHogEndpointQueryDefinition[];

export type PostHogSurveyProgram = {
  readonly key: string;
  readonly name: string;
  readonly status: PostHogSetupStatus;
  readonly targetFlagKey?: string;
  readonly targetCohortName?: string;
  readonly liveUrl?: string;
  readonly questionIntent: string;
};

export const POSTHOG_SURVEY_PROGRAMS = [
  {
    key: "site_nps",
    name: "SEOH Site NPS draft",
    status: "configured-live",
    targetFlagKey: "seoh-survey-nps",
    liveUrl: "https://eu.posthog.com/project/184012/surveys/019e8d31-2b1d-0000-dae1-768a94734df8",
    questionIntent: "Low-volume recommendation/NPS pulse after targeting is wired.",
  },
  {
    key: "post_contact_csat",
    name: "SEOH Post-contact CSAT draft",
    status: "configured-live",
    targetCohortName: "SEOH Contact method users",
    liveUrl: "https://eu.posthog.com/project/184012/surveys/019e8dfc-cc74-0000-785b-17e474d2f945",
    questionIntent: "Ask contact-intent users whether the contact path was clear.",
  },
  {
    key: "resource_usefulness",
    name: "SEOH Resource usefulness draft",
    status: "configured-live",
    targetCohortName: "SEOH Resource engagement users",
    liveUrl: "https://eu.posthog.com/project/184012/surveys/019e8dfe-0b78-0000-d30c-d7754f1585a5",
    questionIntent: "Measure usefulness of resources and lead magnets.",
  },
  {
    key: "pricing_friction",
    name: "SEOH Pricing friction draft",
    status: "configured-live",
    targetCohortName: "SEOH Pricing intent users",
    liveUrl: "https://eu.posthog.com/project/184012/surveys/019e8dfe-4d9b-0000-6d4e-16fb5707bc44",
    questionIntent: "Find pricing-page confusion or missing package information.",
  },
  {
    key: "support_satisfaction",
    name: "SEOH Support satisfaction draft",
    status: "configured-live",
    targetFlagKey: "seoh-support-widget",
    liveUrl: "https://eu.posthog.com/project/184012/surveys/019e8dfe-8d33-0000-ac0a-68ded837981a",
    questionIntent: "Support satisfaction after support widget routing and identity verification exist.",
  },
  {
    key: "portal_onboarding_feedback",
    name: "SEOH Portal onboarding feedback draft",
    status: "configured-live",
    targetFlagKey: "seoh-portal-novu-nudges",
    targetCohortName: "SEOH PWA app users",
    liveUrl: "https://eu.posthog.com/project/184012/surveys/019e8dfe-c5a4-0000-107a-76c7447be150",
    questionIntent: "Portal onboarding feedback for PWA/mobile-shell users.",
  },
  {
    key: "cancellation_reason",
    name: "SEOH Cancellation reason draft",
    status: "configured-live",
    liveUrl: "https://eu.posthog.com/project/184012/surveys/019e8dff-030b-0000-1ef6-32c0443a72e4",
    questionIntent: "Churn/cancellation reason capture for the cancellation route.",
  },
  {
    key: "interview_recruitment",
    name: "SEOH Interview recruitment draft",
    status: "configured-live",
    targetCohortName: "SEOH Booking CTA users",
    liveUrl: "https://eu.posthog.com/project/184012/surveys/019e8dff-40d9-0000-8417-e45d1e7c05f7",
    questionIntent: "Recruit high-intent visitors for founder/operator interviews.",
  },
] as const satisfies readonly PostHogSurveyProgram[];

export type PostHogSupportRoutingPolicy = {
  readonly key: string;
  readonly label: string;
  readonly status: PostHogSetupStatus;
  readonly ownerRole:
    | "founder"
    | "support_owner"
    | "engineering_owner"
    | "privacy_owner"
    | "billing_owner"
    | "content_owner"
    | "compliance_owner"
    | "analytics_owner";
  readonly inboxQueue: string;
  readonly severity: "p0" | "p1" | "p2" | "p3";
  readonly responseTarget: string;
  readonly allowedSources: readonly string[];
  readonly notificationPolicy: string;
  readonly blockedLiveActions: readonly string[];
  readonly routingPolicy: string;
};

export const POSTHOG_SUPPORT_ROUTING_BLOCKED_LIVE_ACTIONS = [
  "posthog_workflow_activation",
  "outbound_customer_message",
  "external_destination_send",
  "slack_or_github_support_integration",
  "email_or_telegram_support_escalation",
  "provider_oauth_connection",
  "activepieces_provider_send",
  "paid_plan_change",
] as const;

export const POSTHOG_SUPPORT_ROUTING_POLICIES = [
  {
    key: "portal_access",
    label: "support:portal-access",
    status: "configured-repo",
    ownerRole: "support_owner",
    inboxQueue: "portal_support_triage",
    severity: "p1",
    responseTarget: "same_business_day",
    allowedSources: ["posthog_support_widget", "posthog_direct_api", "authenticated_client_portal"],
    notificationPolicy: "posthog_inbox_only_no_outbound_ticket_send",
    blockedLiveActions: POSTHOG_SUPPORT_ROUTING_BLOCKED_LIVE_ACTIONS,
    routingPolicy: "Authenticated portal access, login, invite, and portal identity issues route to support triage after identity verification.",
  },
  {
    key: "pwa_install",
    label: "support:pwa-install",
    status: "configured-repo",
    ownerRole: "support_owner",
    inboxQueue: "portal_support_triage",
    severity: "p2",
    responseTarget: "two_business_days",
    allowedSources: ["posthog_support_widget", "authenticated_client_portal", "manual_owner_triage"],
    notificationPolicy: "posthog_inbox_only_no_outbound_ticket_send",
    blockedLiveActions: POSTHOG_SUPPORT_ROUTING_BLOCKED_LIVE_ACTIONS,
    routingPolicy: "PWA install, service-worker, and same-origin subscription questions route to portal support without triggering a notification send.",
  },
  {
    key: "notifications",
    label: "support:notifications",
    status: "configured-repo",
    ownerRole: "support_owner",
    inboxQueue: "portal_support_triage",
    severity: "p1",
    responseTarget: "same_business_day",
    allowedSources: ["posthog_support_widget", "authenticated_client_portal", "posthog_browser_exception_destination"],
    notificationPolicy: "posthog_inbox_only_no_outbound_ticket_send",
    blockedLiveActions: POSTHOG_SUPPORT_ROUTING_BLOCKED_LIVE_ACTIONS,
    routingPolicy: "Browser Web Push and notification preference issues route to support triage; outbound notification proof stays explicitly approval-gated.",
  },
  {
    key: "privacy_data",
    label: "support:privacy-data",
    status: "configured-repo",
    ownerRole: "privacy_owner",
    inboxQueue: "privacy_owner_review",
    severity: "p0",
    responseTarget: "next_business_day_owner_review",
    allowedSources: ["posthog_support_widget", "posthog_direct_api", "manual_owner_triage"],
    notificationPolicy: "posthog_inbox_only_no_outbound_ticket_send",
    blockedLiveActions: POSTHOG_SUPPORT_ROUTING_BLOCKED_LIVE_ACTIONS,
    routingPolicy: "Data access, deletion, consent, and analytics privacy requests route to privacy owner review before any action.",
  },
  {
    key: "cancellation",
    label: "support:cancellation",
    status: "configured-repo",
    ownerRole: "founder",
    inboxQueue: "founder_retention_review",
    severity: "p1",
    responseTarget: "same_business_day",
    allowedSources: ["posthog_support_widget", "authenticated_client_portal", "manual_owner_triage"],
    notificationPolicy: "posthog_inbox_only_no_outbound_ticket_send",
    blockedLiveActions: POSTHOG_SUPPORT_ROUTING_BLOCKED_LIVE_ACTIONS,
    routingPolicy: "Cancellation, churn-risk, and retention-sensitive requests route to founder review with no automated customer contact.",
  },
  {
    key: "billing",
    label: "support:billing",
    status: "configured-repo",
    ownerRole: "billing_owner",
    inboxQueue: "billing_owner_review",
    severity: "p1",
    responseTarget: "same_business_day",
    allowedSources: ["posthog_support_widget", "authenticated_client_portal", "manual_owner_triage"],
    notificationPolicy: "posthog_inbox_only_no_outbound_ticket_send",
    blockedLiveActions: POSTHOG_SUPPORT_ROUTING_BLOCKED_LIVE_ACTIONS,
    routingPolicy: "Billing, invoice, subscription, and payment-source questions route to billing owner review before any provider action.",
  },
  {
    key: "bug",
    label: "support:bug",
    status: "configured-repo",
    ownerRole: "engineering_owner",
    inboxQueue: "engineering_triage",
    severity: "p1",
    responseTarget: "same_business_day",
    allowedSources: ["posthog_support_widget", "posthog_browser_exception_destination", "manual_owner_triage"],
    notificationPolicy: "posthog_inbox_only_existing_exception_push_only",
    blockedLiveActions: POSTHOG_SUPPORT_ROUTING_BLOCKED_LIVE_ACTIONS,
    routingPolicy: "Product bugs, browser exceptions, replay triage, and dead-click friction route to engineering triage; only the existing exception push destination may fire.",
  },
  {
    key: "content_localization",
    label: "support:content-localization",
    status: "configured-repo",
    ownerRole: "content_owner",
    inboxQueue: "content_localization_review",
    severity: "p2",
    responseTarget: "two_business_days",
    allowedSources: ["posthog_support_widget", "manual_owner_triage"],
    notificationPolicy: "posthog_inbox_only_no_outbound_ticket_send",
    blockedLiveActions: POSTHOG_SUPPORT_ROUTING_BLOCKED_LIVE_ACTIONS,
    routingPolicy: "Public copy, locale, and translation feedback routes to content review and must use the public localization gate before release.",
  },
  {
    key: "analytics_alert",
    label: "support:analytics-alert",
    status: "configured-repo",
    ownerRole: "analytics_owner",
    inboxQueue: "analytics_owner_review",
    severity: "p2",
    responseTarget: "two_business_days",
    allowedSources: ["posthog_browser_exception_destination", "manual_owner_triage"],
    notificationPolicy: "posthog_inbox_only_existing_exception_push_only",
    blockedLiveActions: POSTHOG_SUPPORT_ROUTING_BLOCKED_LIVE_ACTIONS,
    routingPolicy: "Endpoint, Web Vitals, replay, and batch-health alerts route to analytics owner review without creating duplicate Activepieces actions.",
  },
  {
    key: "compliance",
    label: "support:compliance",
    status: "configured-repo",
    ownerRole: "compliance_owner",
    inboxQueue: "compliance_owner_review",
    severity: "p0",
    responseTarget: "next_business_day_owner_review",
    allowedSources: ["posthog_support_widget", "posthog_direct_api", "manual_owner_triage"],
    notificationPolicy: "posthog_inbox_only_no_outbound_ticket_send",
    blockedLiveActions: POSTHOG_SUPPORT_ROUTING_BLOCKED_LIVE_ACTIONS,
    routingPolicy: "Adult-content, consent, suppression, and policy-sensitive questions route to compliance owner review before support or automation action.",
  },
] as const satisfies readonly PostHogSupportRoutingPolicy[];

export type PostHogAlertRoutingPolicy = {
  readonly key: string;
  readonly status: PostHogSetupStatus;
  readonly triggerSurface:
    | "browser_exception"
    | "web_vitals"
    | "session_replay"
    | "endpoint_health"
    | "event_acceptance"
    | "analytics_batch"
    | "source_maps"
    | "browser_push"
    | "support_inbox"
    | "otel_ingestion";
  readonly ownerRole:
    | "engineering_owner"
    | "analytics_owner"
    | "support_owner"
    | "release_owner";
  readonly severity: "p0" | "p1" | "p2" | "p3";
  readonly dedupeKey: string;
  readonly suppressionWindow: string;
  readonly allowedRoutes: readonly (
    | "posthog_dashboard_review"
    | "posthog_inbox_only"
    | "existing_browser_exception_push"
    | "piworker_audit_report"
    | "manual_owner_triage"
  )[];
  readonly blockedLiveActions: readonly string[];
  readonly activationGate: string;
  readonly routingPolicy: string;
};

export const POSTHOG_ALERT_ROUTING_BLOCKED_LIVE_ACTIONS = [
  "new_posthog_workflow_activation",
  "new_realtime_destination",
  "telegram_slack_email_github_discord_send",
  "outbound_customer_message",
  "activepieces_lead_or_provider_action",
  "provider_oauth_connection",
  "paid_plan_change",
  "raw_person_or_replay_export",
] as const;

export const POSTHOG_ALERT_ROUTING_POLICIES = [
  {
    key: "browser_exception_spike",
    status: "configured-repo",
    triggerSurface: "browser_exception",
    ownerRole: "engineering_owner",
    severity: "p1",
    dedupeKey: "exception_type:release_id:route_template",
    suppressionWindow: "30m",
    allowedRoutes: ["posthog_dashboard_review", "existing_browser_exception_push", "manual_owner_triage"],
    blockedLiveActions: POSTHOG_ALERT_ROUTING_BLOCKED_LIVE_ACTIONS,
    activationGate: "Use the existing Browser Web Push exception destination only; any additional workflow or channel needs owner approval.",
    routingPolicy: "Route browser exception spikes to engineering triage with release/source-map context and no customer-facing message.",
  },
  {
    key: "web_vitals_regression",
    status: "configured-repo",
    triggerSurface: "web_vitals",
    ownerRole: "engineering_owner",
    severity: "p2",
    dedupeKey: "metric:route_template:device_shell",
    suppressionWindow: "2h",
    allowedRoutes: ["posthog_dashboard_review", "manual_owner_triage"],
    blockedLiveActions: POSTHOG_ALERT_ROUTING_BLOCKED_LIVE_ACTIONS,
    activationGate: "Create a dashboard subscription or workflow only after destination ownership is approved.",
    routingPolicy: "Route Core Web Vitals regressions to engineering review and keep them separate from marketing/CRM automation.",
  },
  {
    key: "replay_capture_gap",
    status: "configured-repo",
    triggerSurface: "session_replay",
    ownerRole: "analytics_owner",
    severity: "p2",
    dedupeKey: "route_family:release_id:consent_state",
    suppressionWindow: "4h",
    allowedRoutes: ["posthog_dashboard_review", "manual_owner_triage"],
    blockedLiveActions: POSTHOG_ALERT_ROUTING_BLOCKED_LIVE_ACTIONS,
    activationGate: "Use replay/dashboard review only until a destination and privacy policy are approved.",
    routingPolicy: "Route replay capture gaps, replay asset failures, or unexpected consent drops to analytics review without exporting raw replay streams.",
  },
  {
    key: "endpoint_empty_unhealthy",
    status: "configured-repo",
    triggerSurface: "endpoint_health",
    ownerRole: "analytics_owner",
    severity: "p2",
    dedupeKey: "endpoint_slug:window_days",
    suppressionWindow: "6h",
    allowedRoutes: ["posthog_dashboard_review", "piworker_audit_report", "manual_owner_triage"],
    blockedLiveActions: POSTHOG_ALERT_ROUTING_BLOCKED_LIVE_ACTIONS,
    activationGate: "Use endpoint QA and piworker audit reports; workflow activation waits for owner-approved destination policy.",
    routingPolicy: "Route unexpected empty or unhealthy endpoint states to analytics review and piworker audit output.",
  },
  {
    key: "event_acceptance_gap",
    status: "configured-repo",
    triggerSurface: "event_acceptance",
    ownerRole: "analytics_owner",
    severity: "p2",
    dedupeKey: "event_name:surface:release_id",
    suppressionWindow: "4h",
    allowedRoutes: ["posthog_dashboard_review", "piworker_audit_report", "manual_owner_triage"],
    blockedLiveActions: POSTHOG_ALERT_ROUTING_BLOCKED_LIVE_ACTIONS,
    activationGate: "Keep event acceptance checks in repo/worker QA until workflow ownership is approved.",
    routingPolicy: "Route missing action/event materialization and event acceptance gaps to analytics review without creating synthetic traffic.",
  },
  {
    key: "analytics_batch_failure",
    status: "configured-repo",
    triggerSurface: "analytics_batch",
    ownerRole: "analytics_owner",
    severity: "p1",
    dedupeKey: "batch_job:run_id:error_class",
    suppressionWindow: "1h",
    allowedRoutes: ["piworker_audit_report", "manual_owner_triage"],
    blockedLiveActions: POSTHOG_ALERT_ROUTING_BLOCKED_LIVE_ACTIONS,
    activationGate: "Use piworker systemd/audit output first; external workflow channels require owner-approved secrets.",
    routingPolicy: "Route PostHog-to-ERPNext batch failures to analytics owner review without retrying with raw person or contact data.",
  },
  {
    key: "source_map_upload_failure",
    status: "configured-repo",
    triggerSurface: "source_maps",
    ownerRole: "release_owner",
    severity: "p1",
    dedupeKey: "release_id:git_commit_sha:upload_error_class",
    suppressionWindow: "1h",
    allowedRoutes: ["posthog_dashboard_review", "manual_owner_triage"],
    blockedLiveActions: POSTHOG_ALERT_ROUTING_BLOCKED_LIVE_ACTIONS,
    activationGate: "Keep CI/Vercel proof as source of truth until release workflow ownership is set.",
    routingPolicy: "Route source-map upload or public map cleanup failures to release review before another deploy marker or key action.",
  },
  {
    key: "browser_push_delivery_failure",
    status: "configured-repo",
    triggerSurface: "browser_push",
    ownerRole: "engineering_owner",
    severity: "p2",
    dedupeKey: "delivery_status:subscription_store:release_id",
    suppressionWindow: "2h",
    allowedRoutes: ["posthog_dashboard_review", "manual_owner_triage"],
    blockedLiveActions: POSTHOG_ALERT_ROUTING_BLOCKED_LIVE_ACTIONS,
    activationGate: "Do not run controlled outbound notification sends without explicit approval.",
    routingPolicy: "Route push route, VAPID, or subscription-store failures to engineering review without sending a test notification.",
  },
  {
    key: "support_ticket_backlog",
    status: "configured-repo",
    triggerSurface: "support_inbox",
    ownerRole: "support_owner",
    severity: "p2",
    dedupeKey: "support_label:priority:sla_state",
    suppressionWindow: "4h",
    allowedRoutes: ["posthog_inbox_only", "manual_owner_triage"],
    blockedLiveActions: POSTHOG_ALERT_ROUTING_BLOCKED_LIVE_ACTIONS,
    activationGate: "Requires live support labels and inbox assignment before any workflow alert is created.",
    routingPolicy: "Route support backlog or SLA-state checks through PostHog inbox-only review until support workflow ownership is approved.",
  },
  {
    key: "otel_ingestion_gap",
    status: "configured-repo",
    triggerSurface: "otel_ingestion",
    ownerRole: "engineering_owner",
    severity: "p3",
    dedupeKey: "otel_signal:service_name:runtime",
    suppressionWindow: "12h",
    allowedRoutes: ["posthog_dashboard_review", "manual_owner_triage"],
    blockedLiveActions: POSTHOG_ALERT_ROUTING_BLOCKED_LIVE_ACTIONS,
    activationGate: "Live OTLP endpoint/header setup and volume policy are still owner-gated.",
    routingPolicy: "Route missing or low-volume OTLP traces/logs to engineering review only after the OTLP ingestion gate is approved.",
  },
] as const satisfies readonly PostHogAlertRoutingPolicy[];

export type PostHogWorkflowDraftPolicy = {
  readonly key: string;
  readonly status: PostHogSetupStatus;
  readonly ownerRole: "analytics_owner";
  readonly activationGate: string;
  readonly qaCommand: string;
  readonly blockedLiveActions: readonly string[];
  readonly draftPolicy: string;
};

export type PostHogWorkflowDraft = {
  readonly key: string;
  readonly status: PostHogSetupStatus;
  readonly alertPolicyKey?:
    | "analytics_batch_failure"
    | "browser_exception_spike"
    | "endpoint_empty_unhealthy"
    | "event_acceptance_gap"
    | "replay_capture_gap"
    | "web_vitals_regression";
  readonly triggerSurface:
    | "analytics_batch"
    | "browser_exception"
    | "endpoint_health"
    | "event_acceptance"
    | "posthog_workflow"
    | "session_replay"
    | "web_vitals";
  readonly ownerRole: "analytics_owner" | "engineering_owner";
  readonly severity: "p1" | "p2" | "p3";
  readonly dedupeKey: string;
  readonly suppressionWindow: string;
  readonly draftTrigger: string;
  readonly draftAction:
    | "internal_triage_note"
    | "piworker_audit_review"
    | "posthog_dashboard_review"
    | "posthog_workflow_history_review";
  readonly allowedRoutes: readonly (
    | "existing_browser_exception_push"
    | "manual_owner_triage"
    | "piworker_audit_report"
    | "posthog_dashboard_review"
    | "posthog_workflow_history_review"
  )[];
  readonly channelPolicy: "existing_browser_exception_destination_only" | "no_channel_send_repo_draft";
  readonly activepiecesOverlapPolicy: string;
  readonly blockedLiveActions: readonly string[];
  readonly activationGate: string;
};

export const POSTHOG_WORKFLOW_DRAFT_BLOCKED_LIVE_ACTIONS = [
  "live_workflow_activation_without_owner_approval",
  "channel_send_without_approved_destination",
  "dashboard_subscription_external_send",
  "outbound_customer_message",
  "activepieces_lead_or_provider_action",
  "provider_oauth_connection",
  "paid_plan_change",
  "raw_person_contact_replay_export",
  "support_label_or_inbox_mutation",
] as const;

export const POSTHOG_WORKFLOW_DRAFT_POLICY = {
  key: "posthog_workflow_draft_guard",
  status: "configured-repo",
  ownerRole: "analytics_owner",
  activationGate:
    "Live PostHog Workflow creation requires owner approval, a named workflow owner, a configured and tested destination/channel, suppression rules, Activepieces non-duplication review, and proof that no raw person/contact/replay/session data or outbound customer send is included.",
  qaCommand: "npm run qa:posthog-workflow-drafts",
  blockedLiveActions: POSTHOG_WORKFLOW_DRAFT_BLOCKED_LIVE_ACTIONS,
  draftPolicy:
    "Workflow drafts are limited to internal triage, dashboard review, workflow-history review, and piworker audit review. They do not create live PostHog workflows, dashboard subscriptions, destinations, support labels, Activepieces actions, provider connections, paid changes, raw data exports, or customer messages.",
} as const satisfies PostHogWorkflowDraftPolicy;

export const POSTHOG_WORKFLOW_DRAFTS = [
  {
    key: "error_spike_exception_triage",
    status: "configured-repo",
    alertPolicyKey: "browser_exception_spike",
    triggerSurface: "browser_exception",
    ownerRole: "engineering_owner",
    severity: "p1",
    dedupeKey: "exception_type:release_id:route_template",
    suppressionWindow: "30m",
    draftTrigger: "Browser Exception action count exceeds the reviewed route/release baseline.",
    draftAction: "posthog_dashboard_review",
    allowedRoutes: ["posthog_dashboard_review", "existing_browser_exception_push", "manual_owner_triage"],
    channelPolicy: "existing_browser_exception_destination_only",
    activepiecesOverlapPolicy: "No Lead, Opportunity, provider-send, or Activepieces flow is created from exception triage.",
    blockedLiveActions: POSTHOG_WORKFLOW_DRAFT_BLOCKED_LIVE_ACTIONS,
    activationGate: "Keep the existing Browser Web Push exception destination active; any new workflow/channel needs owner approval.",
  },
  {
    key: "web_vitals_regression_triage",
    status: "configured-repo",
    alertPolicyKey: "web_vitals_regression",
    triggerSurface: "web_vitals",
    ownerRole: "engineering_owner",
    severity: "p2",
    dedupeKey: "metric:route_template:device_shell",
    suppressionWindow: "2h",
    draftTrigger: "CLS, FCP, LCP, or INP regression persists for a route/device shell window.",
    draftAction: "posthog_dashboard_review",
    allowedRoutes: ["posthog_dashboard_review", "manual_owner_triage"],
    channelPolicy: "no_channel_send_repo_draft",
    activepiecesOverlapPolicy: "Performance regression triage stays engineering-owned and never triggers lead routing or provider sends.",
    blockedLiveActions: POSTHOG_WORKFLOW_DRAFT_BLOCKED_LIVE_ACTIONS,
    activationGate: "Create a workflow only after destination ownership and a non-customer channel are approved.",
  },
  {
    key: "replay_capture_failure_triage",
    status: "configured-repo",
    alertPolicyKey: "replay_capture_gap",
    triggerSurface: "session_replay",
    ownerRole: "analytics_owner",
    severity: "p2",
    dedupeKey: "route_family:release_id:consent_state",
    suppressionWindow: "4h",
    draftTrigger: "Replay capture, replay asset playback, or consented replay volume drops below expected baseline.",
    draftAction: "posthog_dashboard_review",
    allowedRoutes: ["posthog_dashboard_review", "manual_owner_triage"],
    channelPolicy: "no_channel_send_repo_draft",
    activepiecesOverlapPolicy: "Replay diagnostics remain analytics-only and cannot export replay/person rows to Activepieces.",
    blockedLiveActions: POSTHOG_WORKFLOW_DRAFT_BLOCKED_LIVE_ACTIONS,
    activationGate: "Use dashboard/replay review only until privacy and destination ownership are approved.",
  },
  {
    key: "endpoint_empty_unhealthy_triage",
    status: "configured-repo",
    alertPolicyKey: "endpoint_empty_unhealthy",
    triggerSurface: "endpoint_health",
    ownerRole: "analytics_owner",
    severity: "p2",
    dedupeKey: "endpoint_slug:window_days",
    suppressionWindow: "6h",
    draftTrigger: "A non-future-traffic endpoint is unexpectedly empty, unhealthy, or missing the expected fallback row.",
    draftAction: "piworker_audit_review",
    allowedRoutes: ["posthog_dashboard_review", "piworker_audit_report", "manual_owner_triage"],
    channelPolicy: "no_channel_send_repo_draft",
    activepiecesOverlapPolicy: "Endpoint health triage may inspect aggregate QA only and cannot create CRM/provider actions.",
    blockedLiveActions: POSTHOG_WORKFLOW_DRAFT_BLOCKED_LIVE_ACTIONS,
    activationGate: "Keep endpoint checks in repo/worker QA until workflow destination policy is approved.",
  },
  {
    key: "event_acceptance_gap_triage",
    status: "configured-repo",
    alertPolicyKey: "event_acceptance_gap",
    triggerSurface: "event_acceptance",
    ownerRole: "analytics_owner",
    severity: "p2",
    dedupeKey: "event_name:surface:release_id",
    suppressionWindow: "4h",
    draftTrigger: "A registered event/action does not materialize after expected traffic or QA acceptance.",
    draftAction: "piworker_audit_review",
    allowedRoutes: ["posthog_dashboard_review", "piworker_audit_report", "manual_owner_triage"],
    channelPolicy: "no_channel_send_repo_draft",
    activepiecesOverlapPolicy: "Event acceptance triage cannot synthesize traffic, score leads, or call Activepieces flows.",
    blockedLiveActions: POSTHOG_WORKFLOW_DRAFT_BLOCKED_LIVE_ACTIONS,
    activationGate: "Use repo/worker QA evidence until owner-approved workflow routing exists.",
  },
  {
    key: "posthog_workflow_failure_triage",
    status: "configured-repo",
    triggerSurface: "posthog_workflow",
    ownerRole: "analytics_owner",
    severity: "p2",
    dedupeKey: "workflow_key:failure_class:release_id",
    suppressionWindow: "4h",
    draftTrigger: "A future approved PostHog Workflow reports failure, disabled execution, or repeated suppressed runs.",
    draftAction: "posthog_workflow_history_review",
    allowedRoutes: ["posthog_workflow_history_review", "posthog_dashboard_review", "manual_owner_triage"],
    channelPolicy: "no_channel_send_repo_draft",
    activepiecesOverlapPolicy: "Workflow failure triage observes PostHog workflow state only and cannot retry Activepieces/provider actions.",
    blockedLiveActions: POSTHOG_WORKFLOW_DRAFT_BLOCKED_LIVE_ACTIONS,
    activationGate: "Enable only after at least one owner-approved live PostHog Workflow exists and exposes safe execution-state metadata.",
  },
  {
    key: "analytics_batch_failure_triage",
    status: "configured-repo",
    alertPolicyKey: "analytics_batch_failure",
    triggerSurface: "analytics_batch",
    ownerRole: "analytics_owner",
    severity: "p1",
    dedupeKey: "batch_job:run_id:error_class",
    suppressionWindow: "1h",
    draftTrigger: "PostHog-to-ERPNext analytics batch or warehouse aggregate export fails or misses its scheduled window.",
    draftAction: "piworker_audit_review",
    allowedRoutes: ["piworker_audit_report", "manual_owner_triage"],
    channelPolicy: "no_channel_send_repo_draft",
    activepiecesOverlapPolicy: "Batch failure triage is worker/analytics-owned and cannot replay provider sends or customer actions.",
    blockedLiveActions: POSTHOG_WORKFLOW_DRAFT_BLOCKED_LIVE_ACTIONS,
    activationGate: "Use piworker audit/systemd evidence until external workflow channels have approved secrets.",
  },
] as const satisfies readonly PostHogWorkflowDraft[];

export type PostHogExperienceExperimentDraft = {
  readonly key: string;
  readonly name: string;
  readonly status: PostHogSetupStatus;
  readonly liveUrl: string;
  readonly targetFlagKey: string;
  readonly primaryMetricEvent: string;
  readonly globalHoldoutKey: "seoh-experience-global-holdout";
  readonly globalHoldoutPercent: 10;
  readonly holdoutAttachmentState: "attach_to_draft_before_launch";
  readonly rolloutPolicy: string;
};

export const POSTHOG_EXPERIENCE_EXPERIMENT_DRAFTS = [
  {
    key: "seoh-experience-control-baseline",
    name: "SEOH Experience Control Baseline",
    status: "needs-product-decision",
    liveUrl: "https://eu.posthog.com/project/184012/experiments",
    targetFlagKey: "seoh-experience-control-baseline",
    primaryMetricEvent: "cta_engaged",
    globalHoldoutKey: "seoh-experience-global-holdout",
    globalHoldoutPercent: 10,
    holdoutAttachmentState: "attach_to_draft_before_launch",
    rolloutPolicy:
      "Draft-only with 0% current rollout until the ERPNext authoritative experience export is fresh, persona evidence and locale viability exports exist, adult fail-closed QA passes, the 10% global holdout is attached to the draft, and the first traffic-changing run is approved as a capped 5% smoke.",
  },
  {
    key: "seoh-component-rollup-control",
    name: "SEOH Component Rollup Control",
    status: "needs-product-decision",
    liveUrl: "https://eu.posthog.com/project/184012/experiments",
    targetFlagKey: "seoh-component-rollup-control",
    primaryMetricEvent: "component_engaged",
    globalHoldoutKey: "seoh-experience-global-holdout",
    globalHoldoutPercent: 10,
    holdoutAttachmentState: "attach_to_draft_before_launch",
    rolloutPolicy:
      "Draft-only with 0% current rollout until component rollups are healthy, low-cardinality ERPNext writes are proven, persona evidence and locale viability exports exist, the 10% global holdout is attached to the draft, and the first traffic-changing run is approved as a capped 5% smoke.",
  },
] as const satisfies readonly PostHogExperienceExperimentDraft[];

export type PostHogWorkflowLiveActivation = {
  readonly key: string;
  readonly status: PostHogSetupStatus;
  readonly destination:
    | "browser_web_push"
    | "telegram_posthog_alert_webhook"
    | "telegram_posthog_reply_webhook"
    | "posthog_capture"
    | "postiz_public_api";
  readonly sendClass:
    | "internal_alert"
    | "two_way_support_reply"
    | "release_marker"
    | "no_pii_publication_probe";
  readonly requiredEnv: readonly string[];
  readonly liveRoute?: string;
  readonly liveSendAllowed: boolean;
  readonly outboundCustomerMessageAllowed: boolean;
  readonly publicPublishAllowed: boolean;
  readonly qaCommand: string;
  readonly rollback: string;
  readonly dataPolicy: string;
  readonly activationProof: string;
};

export const POSTHOG_WORKFLOW_LIVE_ACTIVATIONS = [
  {
    key: "browser_exception_push_destination",
    status: "configured-live",
    destination: "browser_web_push",
    sendClass: "internal_alert",
    requiredEnv: [
      "POSTHOG_PUSH_ALERT_ENABLED",
      "POSTHOG_PUSH_WEBHOOK_SECRET",
      "WEB_PUSH_OUTBOUND_ALLOWED_HOSTS",
      "WEB_PUSH_VAPID_SUBJECT",
      "NEXT_PUBLIC_PWA_SW_ENABLED",
    ],
    liveRoute: "/api/posthog/push-alert",
    liveSendAllowed: true,
    outboundCustomerMessageAllowed: false,
    publicPublishAllowed: false,
    qaCommand: "npm run qa:posthog-push-alerts",
    rollback: "Disable the PostHog Browser Web Push destination and rotate POSTHOG_PUSH_WEBHOOK_SECRET if delivery is unexpected.",
    dataPolicy:
      "Internal exception alerts may not include raw person, contact, replay, session, request body, provider credential, or customer message data.",
    activationProof: "Browser Web Push exception destination is the only configured-live PostHog alert destination in the tracker.",
  },
  {
    key: "telegram_posthog_alert_destination",
    status: "configured-repo",
    destination: "telegram_posthog_alert_webhook",
    sendClass: "internal_alert",
    requiredEnv: [
      "POSTHOG_ALERT_TELEGRAM_ENABLED",
      "POSTHOG_ALERT_WEBHOOK_SECRET",
      "POSTHOG_OUTBOUND_ALLOWED_HOSTS",
      "TELEGRAM_BOT_TOKEN",
      "TELEGRAM_CHAT_ID",
    ],
    liveRoute: "/api/posthog/telegram-alert",
    liveSendAllowed: false,
    outboundCustomerMessageAllowed: false,
    publicPublishAllowed: false,
    qaCommand: "npm run qa:posthog-telegram-alerts",
    rollback: "Delete the PostHog Telegram alert workflow or destination and rotate the webhook secret.",
    dataPolicy:
      "Internal Telegram alerts may not include raw person, contact, replay, session, URL token, request body, provider credential, or customer message data.",
    activationProof: "Repo route and smoke exist; live workflow/channel activation remains gated until destination secrets are applied.",
  },
  {
    key: "telegram_posthog_support_replies",
    status: "configured-live",
    destination: "telegram_posthog_reply_webhook",
    sendClass: "two_way_support_reply",
    requiredEnv: [
      "POSTHOG_TELEGRAM_REPLIES_ENABLED",
      "POSTHOG_OUTBOUND_ALLOWED_HOSTS",
      "POSTHOG_REPLY_API_KEY",
      "TELEGRAM_BOT_TOKEN",
      "TELEGRAM_CHAT_ID",
      "TELEGRAM_WEBHOOK_SECRET",
    ],
    liveRoute: "/api/posthog/telegram-reply",
    liveSendAllowed: true,
    outboundCustomerMessageAllowed: true,
    publicPublishAllowed: false,
    qaCommand: "npm run qa:posthog-live-workflows",
    rollback: "Disable the Telegram webhook, revoke POSTHOG_REPLY_API_KEY, and rotate TELEGRAM_WEBHOOK_SECRET.",
    dataPolicy:
      "The support reply lane may send only an explicit ticket reply body to the matching PostHog ticket; it must not export raw person, replay, session, provider credential, or unrelated contact data.",
    activationProof: "Two-way support replies are limited to the dedicated Telegram reply route and explicit ticket-scoped replies.",
  },
  {
    key: "release_deploy_marker_send",
    status: "configured-repo",
    destination: "posthog_capture",
    sendClass: "release_marker",
    requiredEnv: [
      "POSTHOG_DEPLOY_MARKER_ENABLED",
      "POSTHOG_DEPLOY_MARKER_LIVE_SEND_APPROVED",
      "POSTHOG_PROJECT_TOKEN or NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN",
    ],
    liveSendAllowed: false,
    outboundCustomerMessageAllowed: false,
    publicPublishAllowed: false,
    qaCommand: "npm run qa:posthog-deploy-marker",
    rollback: "Set POSTHOG_DEPLOY_MARKER_ENABLED=false and rotate the server project token if marker sends are unexpected.",
    dataPolicy:
      "Deploy markers may not include raw person, contact, replay, session, request body, provider credential, customer message, or prompt data.",
    activationProof: "Deploy marker guard is repo-configured and fail-closed until the release-owner env gates are both true.",
  },
  {
    key: "postiz_no_pii_schedule_probe",
    status: "configured-repo",
    destination: "postiz_public_api",
    sendClass: "no_pii_publication_probe",
    requiredEnv: ["POSTIZ_API_KEY", "POSTIZ_API_URL", "ALLOW_POSTIZ_PUBLICATION_SEND"],
    liveSendAllowed: false,
    outboundCustomerMessageAllowed: false,
    publicPublishAllowed: false,
    qaCommand: "npm run qa:postiz-approved-publication-runner",
    rollback: "Disable the Postiz API token, clear queued probes, and keep ALLOW_POSTIZ_PUBLICATION_SEND unset.",
    dataPolicy:
      "Postiz probes may not include raw person, contact, replay, session, customer message, provider credential, or unapproved public content data.",
    activationProof: "Publication runner is fail-closed and requires a separate approved content payload before any schedule or publish.",
  },
] as const satisfies readonly PostHogWorkflowLiveActivation[];

export type PostHogDeployMarkerPolicy = {
  readonly key: string;
  readonly status: PostHogSetupStatus;
  readonly eventName: "seoh_deploy_marker";
  readonly ownerRole: "release_owner";
  readonly activationGate: string;
  readonly allowedRunners: readonly ("local_dry_run" | "ci_after_deploy" | "vercel_post_deploy_hook")[];
  readonly requiredEnv: readonly string[];
  readonly dryRunCommand: string;
  readonly qaCommand: string;
  readonly blockedLiveActions: readonly string[];
  readonly payloadPolicy: string;
};

export const POSTHOG_DEPLOY_MARKER_BLOCKED_LIVE_ACTIONS = [
  "live_marker_send_without_release_owner_approval",
  "new_posthog_workflow_activation",
  "new_realtime_destination",
  "outbound_customer_message",
  "provider_oauth_connection",
  "paid_plan_change",
  "raw_person_or_replay_export",
] as const;

export const POSTHOG_DEPLOY_MARKER_POLICY = {
  key: "deploy_marker_guard",
  status: "configured-repo",
  eventName: "seoh_deploy_marker",
  ownerRole: "release_owner",
  activationGate:
    "Live marker capture requires POSTHOG_DEPLOY_MARKER_ENABLED=true, POSTHOG_DEPLOY_MARKER_LIVE_SEND_APPROVED=true, and an approved project token in the deployment secret store.",
  allowedRunners: ["local_dry_run", "ci_after_deploy", "vercel_post_deploy_hook"],
  requiredEnv: [
    "POSTHOG_DEPLOY_MARKER_ENABLED",
    "POSTHOG_DEPLOY_MARKER_LIVE_SEND_APPROVED",
    "POSTHOG_PROJECT_TOKEN or NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN",
  ],
  dryRunCommand: "npm run posthog:deploy-marker:dry-run",
  qaCommand: "npm run qa:posthog-deploy-marker",
  blockedLiveActions: POSTHOG_DEPLOY_MARKER_BLOCKED_LIVE_ACTIONS,
  payloadPolicy:
    "Marker payloads are limited to release/deployment IDs, git commit/branch, deploy environment, provider, status, and deployment host. No person, contact, replay, session, ticket, or provider credential data is allowed.",
} as const satisfies PostHogDeployMarkerPolicy;

export type PostHogLogProcessorPolicy = {
  readonly key: string;
  readonly status: PostHogSetupStatus;
  readonly ownerRole: "engineering_owner";
  readonly activationGate: string;
  readonly requiredEnv: readonly string[];
  readonly qaCommand: string;
  readonly blockedLiveActions: readonly string[];
  readonly payloadPolicy: string;
};

export const POSTHOG_LOG_PROCESSOR_BLOCKED_LIVE_ACTIONS = [
  "live_log_ingestion_without_owner_approval",
  "raw_console_or_request_body_capture",
  "raw_person_or_replay_export",
  "new_log_destination",
  "provider_oauth_connection",
  "paid_plan_change",
  "outbound_customer_message",
] as const;

export const POSTHOG_LOG_PROCESSOR_POLICY = {
  key: "otel_log_processor_guard",
  status: "configured-repo",
  ownerRole: "engineering_owner",
  activationGate:
    "Live server/worker log ingestion requires SEOH_OTEL_LOGS_ENABLED=true, POSTHOG_OTEL_LOGS_LIVE_SEND_APPROVED=true, an approved OTLP logs endpoint, and documented sampling/volume limits.",
  requiredEnv: ["SEOH_OTEL_LOGS_ENABLED", "POSTHOG_OTEL_LOGS_LIVE_SEND_APPROVED", "OTEL_EXPORTER_OTLP_LOGS_ENDPOINT"],
  qaCommand: "npm run qa:posthog-log-processor",
  blockedLiveActions: POSTHOG_LOG_PROCESSOR_BLOCKED_LIVE_ACTIONS,
  payloadPolicy:
    "Log records are limited to sanitized low-cardinality release, route, job, status, and count attributes. Raw console text, request bodies, emails, phone values, token-like values, person/replay/session IDs, and provider credentials are dropped or redacted before export.",
} as const satisfies PostHogLogProcessorPolicy;

export type PostHogLocalEvaluationPolicy = {
  readonly key: string;
  readonly status: PostHogSetupStatus;
  readonly ownerRole: "engineering_owner";
  readonly activationGate: string;
  readonly requiredEnv: readonly string[];
  readonly qaCommand: string;
  readonly blockedLiveActions: readonly string[];
  readonly payloadPolicy: string;
};

export const POSTHOG_LOCAL_EVALUATION_BLOCKED_LIVE_ACTIONS = [
  "server_local_evaluation_without_owner_approval",
  "private_key_in_public_env",
  "unknown_flag_bootstrap",
  "payload_bootstrap_without_schema",
  "provider_oauth_connection",
  "paid_plan_change",
  "outbound_customer_message",
] as const;

export const POSTHOG_LOCAL_EVALUATION_POLICY = {
  key: "local_evaluation_bootstrap_guard",
  status: "configured-repo",
  ownerRole: "engineering_owner",
  activationGate:
    "Live server-side local evaluation requires SEOH_POSTHOG_LOCAL_EVALUATION_ENABLED=true, POSTHOG_LOCAL_EVALUATION_LIVE_APPROVED=true, and a private key stored only in an approved server or worker secret store.",
  requiredEnv: [
    "SEOH_POSTHOG_LOCAL_EVALUATION_ENABLED",
    "POSTHOG_LOCAL_EVALUATION_LIVE_APPROVED",
    "POSTHOG_LOCAL_EVALUATION_PRIVATE_KEY",
  ],
  qaCommand: "npm run qa:posthog-local-evaluation",
  blockedLiveActions: POSTHOG_LOCAL_EVALUATION_BLOCKED_LIVE_ACTIONS,
  payloadPolicy:
    "Bootstrap snapshots are limited to registered POSTHOG_FEATURE_FLAG_SHELLS keys with boolean values. Unknown flags, non-boolean values, remote-config payload bypasses, person/contact/replay/session data, provider credentials, and customer-message triggers are blocked.",
} as const satisfies PostHogLocalEvaluationPolicy;

export type PostHogAiObservabilityPolicy = {
  readonly key: string;
  readonly status: PostHogSetupStatus;
  readonly eventName: "seoh_ai_observation";
  readonly ownerRole: "engineering_owner";
  readonly activationGate: string;
  readonly requiredEnv: readonly string[];
  readonly qaCommand: string;
  readonly blockedLiveActions: readonly string[];
  readonly payloadPolicy: string;
};

export const POSTHOG_AI_OBSERVABILITY_BLOCKED_LIVE_ACTIONS = [
  "live_ai_observation_without_owner_approval",
  "raw_prompt_or_completion_capture",
  "provider_api_key_or_header_capture",
  "raw_person_or_replay_export",
  "new_posthog_workflow_activation",
  "provider_oauth_connection",
  "paid_plan_change",
  "outbound_customer_message",
] as const;

export const POSTHOG_AI_OBSERVABILITY_POLICY = {
  key: "ai_observability_guard",
  status: "configured-repo",
  eventName: "seoh_ai_observation",
  ownerRole: "engineering_owner",
  activationGate:
    "Live AI observability export requires SEOH_POSTHOG_AI_OBSERVABILITY_ENABLED=true, POSTHOG_AI_OBSERVABILITY_LIVE_SEND_APPROVED=true, and an approved PostHog project token stored only in a server or worker secret store.",
  requiredEnv: [
    "SEOH_POSTHOG_AI_OBSERVABILITY_ENABLED",
    "POSTHOG_AI_OBSERVABILITY_LIVE_SEND_APPROVED",
    "POSTHOG_AI_OBSERVABILITY_PROJECT_TOKEN or POSTHOG_PROJECT_TOKEN",
  ],
  qaCommand: "npm run qa:posthog-ai-observability",
  blockedLiveActions: POSTHOG_AI_OBSERVABILITY_BLOCKED_LIVE_ACTIONS,
  payloadPolicy:
    "AI observability records are limited to provider, model, operation, task, status, latency, token counts, cost, release, route, locale, job, cache, and allow-listed low-cardinality metadata. Raw prompts, completions, messages, person/contact/replay/session data, provider credentials, request/response bodies, and outbound-send triggers are blocked.",
} as const satisfies PostHogAiObservabilityPolicy;

export type PostHogPromptEvaluationPolicy = {
  readonly key: string;
  readonly status: PostHogSetupStatus;
  readonly eventName: "seoh_prompt_evaluation";
  readonly ownerRole: "product";
  readonly activationGate: string;
  readonly requiredEnv: readonly string[];
  readonly qaCommand: string;
  readonly blockedLiveActions: readonly string[];
  readonly payloadPolicy: string;
};

export const POSTHOG_PROMPT_EVALUATION_BLOCKED_LIVE_ACTIONS = [
  "live_prompt_evaluation_without_owner_approval",
  "raw_prompt_or_completion_capture",
  "raw_evaluation_sample_capture",
  "provider_api_key_or_header_capture",
  "raw_person_contact_replay_export",
  "production_prompt_mutation",
  "new_posthog_workflow_activation",
  "provider_oauth_connection",
  "paid_plan_change",
  "outbound_customer_message",
] as const;

export const POSTHOG_PROMPT_EVALUATION_POLICY = {
  key: "prompt_evaluation_guard",
  status: "configured-repo",
  eventName: "seoh_prompt_evaluation",
  ownerRole: "product",
  activationGate:
    "Live prompt evaluation export requires SEOH_POSTHOG_PROMPT_EVALUATION_ENABLED=true, POSTHOG_PROMPT_EVALUATION_LIVE_SEND_APPROVED=true, prompt/rubric ownership, and an approved PostHog project token stored only in a server or worker secret store.",
  requiredEnv: [
    "SEOH_POSTHOG_PROMPT_EVALUATION_ENABLED",
    "POSTHOG_PROMPT_EVALUATION_LIVE_SEND_APPROVED",
    "POSTHOG_PROMPT_EVALUATION_PROJECT_TOKEN or POSTHOG_PROJECT_TOKEN",
  ],
  qaCommand: "npm run qa:posthog-prompt-evaluation",
  blockedLiveActions: POSTHOG_PROMPT_EVALUATION_BLOCKED_LIVE_ACTIONS,
  payloadPolicy:
    "Prompt evaluation records are limited to prompt key/version labels, evaluation family, rubric key, task, surface, status, score, sample counts, failure counts, latency, provider/model labels, release, route, locale, and allow-listed low-cardinality metadata. Raw prompts, completions, messages, test samples, person/contact/replay/session data, provider credentials, request/response bodies, production prompt mutation, and outbound-send triggers are blocked.",
} as const satisfies PostHogPromptEvaluationPolicy;

export type PostHogEmbeddedAnalyticsPolicy = {
  readonly key: string;
  readonly status: PostHogSetupStatus;
  readonly ownerRole: "analytics_owner";
  readonly activationGate: string;
  readonly requiredEnv: readonly string[];
  readonly qaCommand: string;
  readonly blockedLiveActions: readonly string[];
  readonly payloadPolicy: string;
};

export const POSTHOG_EMBEDDED_ANALYTICS_BLOCKED_LIVE_ACTIONS = [
  "live_endpoint_query_without_owner_approval",
  "personal_api_key_in_browser_or_repo",
  "raw_person_contact_replay_export",
  "unsanitized_endpoint_row_embed",
  "unknown_endpoint_slug_embed",
  "provider_oauth_connection",
  "paid_plan_change",
  "outbound_customer_message",
] as const;

export const POSTHOG_EMBEDDED_ANALYTICS_POLICY = {
  key: "embedded_internal_analytics_guard",
  status: "configured-repo",
  ownerRole: "analytics_owner",
  activationGate:
    "Live embedded/internal endpoint queries require SEOH_POSTHOG_EMBEDDED_ANALYTICS_ENABLED=true, POSTHOG_EMBEDDED_ANALYTICS_LIVE_QUERY_APPROVED=true, and a PostHog API key stored only in a server or worker secret store.",
  requiredEnv: [
    "SEOH_POSTHOG_EMBEDDED_ANALYTICS_ENABLED",
    "POSTHOG_EMBEDDED_ANALYTICS_LIVE_QUERY_APPROVED",
    "POSTHOG_EMBEDDED_ANALYTICS_API_KEY or POSTHOG_PERSONAL_API_KEY",
  ],
  qaCommand: "npm run qa:posthog-embedded-analytics",
  blockedLiveActions: POSTHOG_EMBEDDED_ANALYTICS_BLOCKED_LIVE_ACTIONS,
  payloadPolicy:
    "Embedded/internal analytics rows are limited to registered POSTHOG_ENDPOINT_DEFINITIONS, endpoint consumers, aggregate metrics, and allow-listed low-cardinality dimensions. Unknown endpoints, raw person/contact/replay/session IDs, provider credentials, request bodies, browser-exposed API keys, provider connections, paid changes, and outbound messages are blocked.",
} as const satisfies PostHogEmbeddedAnalyticsPolicy;

export type PostHogSetupSurface = {
  readonly surface: string;
  readonly status: PostHogSetupStatus;
  readonly owner:
    | "posthog_live"
    | "repo"
    | "piworker"
    | "piwarehouse"
    | "pierpnext"
    | "piautomation"
    | "owner_account"
    | "product";
  readonly nextAction: string;
};

export type PostHogGroupAnalyticsPolicy = {
  readonly key: string;
  readonly status: PostHogSetupStatus;
  readonly ownerRole: "owner_account" | "product";
  readonly qaCommand: string;
  readonly paidAddOnGate: string;
  readonly approvedGroupTypes: readonly ("company" | "account" | "customer")[];
  readonly blockedLiveActions: readonly string[];
  readonly groupKeyPolicy: string;
};

export type PostHogWarehouseSourcePlan = {
  readonly key: string;
  readonly sourceFamily: string;
  readonly status: PostHogSetupStatus;
  readonly setupMode: "managed-source" | "self-hosted-object-storage" | "batch-export-destination" | "saved-model";
  readonly owner: "owner_account" | "piwarehouse" | "pierpnext" | "piautomation" | "product";
  readonly recommendedOrder: number;
  readonly recommendedUse: string;
  readonly blocker: string;
  readonly docsUrl: string;
};

export const POSTHOG_WAREHOUSE_SOURCE_PLAN = [
  {
    key: "r2_aggregate_parquet",
    sourceFamily: "Cloudflare R2 or S3-compatible object storage",
    status: "partial-live",
    setupMode: "self-hosted-object-storage",
    owner: "piwarehouse",
    recommendedOrder: 1,
    recommendedUse: "Cheapest first warehouse path for aggregate endpoint snapshots, campaign rollups, and DuckDB/CSV QA without raw person/contact/replay streams.",
    blocker: "Aggregate CSV export, EU R2 bucket, host-only writer/reader keys, PostHog self-managed source, and piworker daily export/retention timer are live for the approved ten endpoints. Compact aggregate tables, saved models, wider row budgets, and any extra source remain separate owner/metric gates.",
    docsUrl: "https://posthog.com/docs/cdp/sources",
  },
  {
    key: "stripe_payments",
    sourceFamily: "Stripe",
    status: "needs-owner-oauth",
    setupMode: "managed-source",
    owner: "owner_account",
    recommendedOrder: 2,
    recommendedUse: "Revenue, churn, customer value, and paid conversion attribution once a payment account exists.",
    blocker: "Needs Stripe account owner OAuth/API authorization and revenue-property naming.",
    docsUrl: "https://posthog.com/docs/cdp/sources",
  },
  {
    key: "supabase_portal",
    sourceFamily: "Supabase or Postgres",
    status: "needs-owner-oauth",
    setupMode: "managed-source",
    owner: "owner_account",
    recommendedOrder: 3,
    recommendedUse: "Portal account, subscription, and authenticated product-usage joins after portal schema ownership exists.",
    blocker: "Needs read-only source credentials, table allow-list, primary-key/replication-key choices, and row-volume budget.",
    docsUrl: "https://posthog.com/docs/cdp/sources",
  },
  {
    key: "erpnext_crm_aggregates",
    sourceFamily: "ERPNext/Frappe via MySQL-compatible replica or aggregate export",
    status: "needs-secret-gate",
    setupMode: "managed-source",
    owner: "pierpnext",
    recommendedOrder: 4,
    recommendedUse: "CRM stage attribution, lead/opportunity funnel joins, and support/ops dashboards from approved aggregate tables.",
    blocker: "Needs read-only replica/export path; do not connect the live ERPNext production database directly until table allow-list and backup/rollback rules exist.",
    docsUrl: "https://posthog.com/docs/cdp/sources",
  },
  {
    key: "mautic_marketing_aggregates",
    sourceFamily: "Mautic via MySQL-compatible replica or aggregate export",
    status: "needs-owner-oauth",
    setupMode: "managed-source",
    owner: "owner_account",
    recommendedOrder: 5,
    recommendedUse: "Campaign membership, email/form engagement, and suppression-safe lifecycle attribution after Mautic exists.",
    blocker: "Needs Mautic workspace, read-only export/replica credentials, suppression model, and no-send activation gate.",
    docsUrl: "https://posthog.com/docs/cdp/sources",
  },
  {
    key: "support_ticket_source",
    sourceFamily: "Intercom, Zendesk, Freshdesk, Plain, Gorgias, or support aggregate export",
    status: "needs-owner-oauth",
    setupMode: "managed-source",
    owner: "owner_account",
    recommendedOrder: 6,
    recommendedUse: "Ticket volume, support health, replay/error impact, and customer-risk joins after support routing is owned.",
    blocker: "Needs support tool selection/OAuth and ticket-field policy.",
    docsUrl: "https://posthog.com/docs/cdp/sources",
  },
  {
    key: "paid_ads_sources",
    sourceFamily: "Google Ads, Meta Ads, LinkedIn Ads, Bing Ads, TikTok Ads, Reddit Ads, Pinterest Ads, or Snapchat Ads",
    status: "needs-owner-oauth",
    setupMode: "managed-source",
    owner: "owner_account",
    recommendedOrder: 7,
    recommendedUse: "Paid campaign cost and conversion joins when ad accounts exist.",
    blocker: "Needs ad account owner OAuth, naming contract, cost budget, and attribution model.",
    docsUrl: "https://posthog.com/docs/cdp/sources",
  },
  {
    key: "warehouse_saved_models",
    sourceFamily: "PostHog SQL editor and warehouse models",
    status: "partial-live",
    setupMode: "saved-model",
    owner: "product",
    recommendedOrder: 8,
    recommendedUse: "Saved views for campaign ROI, CRM stage attribution, portal usage, support volume, error impact, and batch freshness after source ownership exists.",
    blocker: "Needs at least one approved source or aggregate object-storage table plus metric-owner signoff.",
    docsUrl: "https://posthog.com/docs/data-warehouse",
  },
] as const satisfies readonly PostHogWarehouseSourcePlan[];

export const POSTHOG_GROUP_ANALYTICS_GROUP_TYPES = ["company", "account", "customer"] as const;

export const POSTHOG_GROUP_ANALYTICS_BLOCKED_LIVE_ACTIONS = [
  "group_company_name_or_domain",
  "group_customer_name_or_contact",
  "group_crm_record_id_or_adult_review_id",
  "live_group_call_without_paid_addon",
  "provider_oauth_connection",
  "paid_plan_change",
  "outbound_customer_message",
] as const;

export const POSTHOG_GROUP_ANALYTICS_POLICY = {
  key: "group_analytics_guard",
  status: "configured-repo",
  ownerRole: "owner_account",
  qaCommand: "npm run qa:posthog-group-analytics",
  paidAddOnGate: "Paid group analytics approval is required before any live company/account/customer group() call is enabled.",
  approvedGroupTypes: POSTHOG_GROUP_ANALYTICS_GROUP_TYPES,
  blockedLiveActions: POSTHOG_GROUP_ANALYTICS_BLOCKED_LIVE_ACTIONS,
  groupKeyPolicy:
    "Only opaque server-issued UUID or hex IDs may be hashed into company/account/customer keys. Raw company names, domains, customer names, CRM record IDs, and adult-review identifiers are blocked.",
} as const satisfies PostHogGroupAnalyticsPolicy;

export const POSTHOG_SETUP_SURFACES = [
  {
    surface: "Organization/project basics",
    status: "configured-live",
    owner: "posthog_live",
    nextAction: "Keep non-secret project ID, EU region, proxy host, and owner role notes current.",
  },
  {
    surface: "Team access and roles",
    status: "needs-live-setup",
    owner: "owner_account",
    nextAction: "Invite required users and assign owner/admin/member/viewer roles.",
  },
  {
    surface: "Billing, usage, and product limits",
    status: "needs-owner-decision",
    owner: "owner_account",
    nextAction: "Record paid add-ons, usage caps, replay retention, group analytics, workflows, logs, and warehouse limits.",
  },
  {
    surface: "API access and schemas",
    status: "needs-secret-gate",
    owner: "owner_account",
    nextAction: "Create least-privilege restricted keys only after worker secret-store approval.",
  },
  {
    surface: "SDK install and capture hosts",
    status: "configured-repo",
    owner: "repo",
    nextAction: "Keep consent-gated PostHog SDK and managed proxy configuration in staging/build QA.",
  },
  {
    surface: "WAF/IP allowlist for PostHog services",
    status: "needs-live-setup",
    owner: "owner_account",
    nextAction: "Add official PostHog service IPs only if snapshots/heatmaps are blocked.",
  },
  {
    surface: "Event taxonomy and payload schema",
    status: "configured-repo",
    owner: "repo",
    nextAction: "Update lib/posthog/events.ts and QA before emitting new events.",
  },
  {
    surface: "Event/action definitions",
    status: "configured-live",
    owner: "posthog_live",
    nextAction: "Maintain the 12 live action definitions in POSTHOG_ACTION_DEFINITIONS.",
  },
  {
    surface: "Property definitions",
    status: "partial-live",
    owner: "posthog_live",
    nextAction: "Maintain the 254 observed property definitions; Chrome UI audit checked 52 SEOH low-cardinality/release keys with 26 materialized and 26 future-traffic/materialization keys. Use an approved worker/server API path for future full-metadata exports because direct browser API URLs can be blocked.",
  },
  {
    surface: "Cohorts and segments",
    status: "configured-live",
    owner: "posthog_live",
    nextAction: "Maintain the 12 dynamic 30-day cohorts in POSTHOG_COHORT_DEFINITIONS.",
  },
  {
    surface: "Web analytics",
    status: "configured-live",
    owner: "posthog_live",
    nextAction: "Maintain authorized URL, replay/error tables, Web Vitals views, and the current Web analytics goal rows: SEOH Contact method intent, SEOH Web vitals signal, SEOH Dead click friction, SEOH Browser exception, and SEOH Audience scope confidence.",
  },
  {
    surface: "Marketing analytics beta",
    status: "needs-owner-oauth",
    owner: "owner_account",
    nextAction: "Connect ad platforms only after account-owner OAuth and attribution policy approval.",
  },
  {
    surface: "Product analytics",
    status: "configured-live",
    owner: "posthog_live",
    nextAction: "Keep saved insights aligned to actions, cohorts, endpoints, and setup-health checks.",
  },
  {
    surface: "Dashboards",
    status: "configured-live",
    owner: "posthog_live",
    nextAction: "Maintain the seven SEOH dashboards, including SEOH PostHog Full Setup Health at /dashboard/722753.",
  },
  {
    surface: "Heatmaps, dead clicks, rage clicks",
    status: "configured-live",
    owner: "posthog_live",
    nextAction: "Maintain the 21 live SEOH heatmaps, dead-click/rage-click surfaces, and snapshot/WAF health checks.",
  },
  {
    surface: "Session replay capture",
    status: "configured-live",
    owner: "posthog_live",
    nextAction: "Maintain live recordings, synthetic collections, and SEOH Conversion & Error Replay Triage at /replay/playlists/Im1Y2NqT.",
  },
  {
    surface: "Session replay retention",
    status: "needs-owner-decision",
    owner: "owner_account",
    nextAction: "Decide whether paid retention above 30 days is worth it for QA/support workflows.",
  },
  {
    surface: "Error tracking browser",
    status: "configured-live",
    owner: "posthog_live",
    nextAction: "Keep exception capture active; Vercel deployment BUAP8Gix4 created the PostHog release and uploaded 20 source-map symbol sets. Assignment rules, suppression policy, and alert destinations remain separate gates.",
  },
  {
    surface: "Error tracking server/Next.js",
    status: "configured-repo",
    owner: "repo",
    nextAction: "Validate onRequestError $exception acceptance after deployment; source-map upload is handled by the production postbuild workflow when enabled.",
  },
  {
    surface: "Linear/GitHub/GitLab/Jira issue links",
    status: "partial-live",
    owner: "owner_account",
    nextAction: "Keep Linear connected; add GitHub/GitLab/Jira only after owner OAuth approval.",
  },
  {
    surface: "Logs",
    status: "partial-live",
    owner: "repo",
    nextAction: "Browser console logs are live. POSTHOG_LOG_PROCESSOR_POLICY and npm run qa:posthog-log-processor define the no-secret server/worker log processor guard; live log ingestion still requires an approved PostHog OTLP endpoint/header setup, volume policy, and POSTHOG_OTEL_LOGS_LIVE_SEND_APPROVED=true.",
  },
  {
    surface: "Tracing",
    status: "configured-repo",
    owner: "repo",
    nextAction: "instrumentation.ts now starts @vercel/otel only in node runtime when SEOH_OTEL_ENABLED=true and an OTLP endpoint or Vercel trace drain exists; lib/posthog/otel.ts provides sanitized resource attributes and withPostHogTraceSpan, with the first portal-dashboard span wired. Live PostHog trace ingestion remains gated on endpoint/header setup and volume limits.",
  },
  {
    surface: "Feature flags",
    status: "configured-live",
    owner: "posthog_live",
    nextAction: "Keep repo readers, repo-hook mapping, schema-validated payload helpers, browser snapshots, and default-off fallbacks aligned to the seven live shells.",
  },
  {
    surface: "Remote config / payload flags",
    status: "configured-repo",
    owner: "repo",
    nextAction: "lib/posthog/flag-payloads.ts and window.__seohPostHogGetRemoteConfigPayload validate payloads for support widget placement, survey targeting, portal nudges, and experience registry variants; npm run qa:posthog-flag-payloads guards the repo path. Live PostHog payload values and targeting remain owner-gated.",
  },
  {
    surface: "Local evaluation / bootstrapping",
    status: "configured-repo",
    owner: "repo",
    nextAction: "POSTHOG_LOCAL_EVALUATION_POLICY, lib/posthog/local-evaluation.ts, and npm run qa:posthog-local-evaluation define the no-secret bootstrap guard. Live local evaluation still requires owner approval and a server-only private key.",
  },
  {
    surface: "Local evaluation guard",
    status: "configured-repo",
    owner: "repo",
    nextAction: "Use sanitizePostHogBootstrapFlags and buildPostHogBootstrapFlagSnapshot before bootstrapping any server-evaluated flags. Unknown flags, non-boolean values, payload bypasses, public private keys, provider OAuth, paid changes, and outbound customer messages stay blocked.",
  },
  {
    surface: "Early access management",
    status: "needs-live-setup",
    owner: "posthog_live",
    nextAction: "Create opt-in groups for beta portal, support widget, survey program, and app/PWA changes.",
  },
  {
    surface: "Experiments",
    status: "needs-product-decision",
    owner: "posthog_live",
    nextAction: "Default experiment settings are visible; create experiments only after primary metrics, guardrails, flags, and sample rules are chosen.",
  },
  {
    surface: "Surveys",
    status: "configured-live",
    owner: "posthog_live",
    nextAction: "Keep all eight draft survey shells cataloged by live ID; launch only after target flags, cohorts, routes, and copy are approved.",
  },
  {
    surface: "Support widget and inbox",
    status: "partial-live",
    owner: "repo",
    nextAction: "Support, allowed domain, widget, Direct API, identity-verification controls, and empty Tickets inbox with status/priority/channel/SLA/tag/assignee filters are verified. Hashed authenticated portal-only identify/support identity binding is repo-implemented; POSTHOG_SUPPORT_ROUTING_POLICIES catalogs ten no-secret routing labels with inbox owner roles and no-outbound notification policy. Live labels, workflow activation, integrations, and ticket sends remain owner-gated.",
  },
  {
    surface: "Support HMAC identity verification",
    status: "configured-repo",
    owner: "repo",
    nextAction: "Server portal ID hashing, HMAC helper, browser identify/setIdentity hooks, and client-portal binders are ready. The portal hashes the Supabase auth user ID into portal_user:<sha256> after server authentication, then signs only that hashed distinct ID; it never sends raw Supabase IDs, email, search params, or arbitrary visitor input to PostHog identity. After 2026-06-04 browser-output exposure, the PostHog Support secret was rotated, Vercel Production was updated with the new masked POSTHOG_SUPPORT_IDENTITY_SECRET, and the PostHog backup key was deleted.",
  },
  {
    surface: "Support integrations",
    status: "needs-owner-oauth",
    owner: "owner_account",
    nextAction: "Connect Slack/GitHub support integrations only through owner authorization.",
  },
  {
    surface: "Data pipelines transformations",
    status: "configured-live",
    owner: "posthog_live",
    nextAction: "Maintain active transformations: Filter Bot Events, Drop QA mode events, User Agent Populator, and GeoIP.",
  },
  {
    surface: "Data pipelines realtime destinations",
    status: "partial-live",
    owner: "posthog_live",
    nextAction: "Browser Web Push HTTP Webhook destination 019e9209-da75-0000-992f-3496944b5f4a is active for Exception events; configure only additional intentional destinations and avoid duplicating Activepieces lead routing.",
  },
  {
    surface: "Batch exports",
    status: "partial-live",
    owner: "piworker",
    nextAction: "Aggregate CSV export is live through scripts/posthog-warehouse-aggregate-export.mjs and the piworker seoh-posthog-warehouse-r2-export.timer. npm run qa:posthog-warehouse-export covers schema, row counts, no-PII output, and zero-row fallback health rows; npm run qa:posthog-warehouse-r2-daily-export covers the wrapper smoke. Keep the endpoint set at the approved ten slugs and --max-rows-per-endpoint 250 until a separate row-budget gate widens it.",
  },
  {
    surface: "Self-hosted warehouse mirror",
    status: "planned-hardware",
    owner: "piwarehouse",
    nextAction: "Prefer PostHog built-in SQL/endpoints plus the active piworker R2 aggregate snapshot timer first. If volume or ownership needs grow, provision a low-cost piwarehouse host for DuckDB/CSV/R2 compaction and QA. Keep raw lead/contact/replay streams out of this path.",
  },
  {
    surface: "Data warehouse sources",
    status: "partial-live",
    owner: "owner_account",
    nextAction: "The first approved self-managed Cloudflare R2 source is live for seoh_posthog_aggregate_endpoint_snapshots. Use POSTHOG_WAREHOUSE_SOURCE_PLAN for remaining source order: Stripe, Supabase/Postgres, ERPNext aggregate replica/export, Mautic aggregate export, support tickets, and ad sources only after credentials/OAuth/table allow-lists exist.",
  },
  {
    surface: "SQL editor and warehouse models",
    status: "partial-live",
    owner: "posthog_live",
    nextAction: "SQL editor is available; create saved models/views after data sources, destination policy, and metric ownership exist.",
  },
  {
    surface: "Revenue analytics/properties",
    status: "watch-docs",
    owner: "product",
    nextAction: "Track revenue through custom properties/dashboards instead of relying on the deprecated revenue dashboard.",
  },
  {
    surface: "Customer analytics",
    status: "needs-live-setup",
    owner: "posthog_live",
    nextAction: "Configure customer profiles, usage metrics, and customer lists after identity rules are approved.",
  },
  {
    surface: "Group analytics",
    status: "configured-repo",
    owner: "repo",
    nextAction:
      "POSTHOG_GROUP_ANALYTICS_POLICY and lib/posthog/group-analytics.ts keep company/account/customer group keys opaque and block live group() calls until the paid add-on and owner approval are both granted.",
  },
  {
    surface: "Workflows",
    status: "configured-repo",
    owner: "repo",
    nextAction: "POSTHOG_WORKFLOW_DRAFT_POLICY, POSTHOG_WORKFLOW_DRAFTS, docs/posthog-workflow-draft-policy.md, lib/posthog/workflow-drafts.ts, and npm run qa:posthog-workflow-drafts define seven no-secret workflow drafts for exception, Web Vitals, replay, endpoint, event acceptance, workflow failure, and batch failure triage. Live workflow activation, subscriptions, destinations, and channel sends remain owner-gated.",
  },
  {
    surface: "Workflow draft guard",
    status: "configured-repo",
    owner: "repo",
    nextAction: "Use POSTHOG_WORKFLOW_DRAFTS before any live PostHog Workflow change. Drafts keep assignment, suppression, channels, and Activepieces non-duplication explicit without creating a live workflow or outbound send.",
  },
  {
    surface: "Workflow channels",
    status: "needs-owner-oauth",
    owner: "owner_account",
    nextAction: "Configure email, Slack, Twilio, or other channels with owner authorization.",
  },
  {
    surface: "Telegram alert bridge",
    status: "configured-repo",
    owner: "repo",
    nextAction: "Deploy /api/posthog/telegram-alert, set POSTHOG_ALERT_WEBHOOK_SECRET plus Telegram bot/chat secrets, then connect a PostHog Workflow or real-time destination with a shared-secret header.",
  },
  {
    surface: "Browser Web Push bridge",
    status: "configured-live",
    owner: "posthog_live",
    nextAction: "After browser-output exposure of the destination bearer value, PostHog destination 019e9209-da75-0000-992f-3496944b5f4a and Vercel Production/Preview POSTHOG_PUSH_WEBHOOK_SECRET were rotated again. Vercel redeploy Ey5mtzwnun1JQYdyHHyXwbNTfA6u reached Ready; live route smoke returned 200 for the valid replacement secret with delivery.status=no_subscriptions before a subscription existed, 401 for a wrong secret, and GET /api/pwa/vapid-public-key returns 200. A fresh approved Chrome profile then produced Vercel runtime proof for GET /api/pwa/vapid-public-key 200 and POST /api/pwa/push-subscription 201 on 2026-06-04. Keep outbound notification send proof explicitly approval-gated.",
  },
  {
    surface: "Slack app",
    status: "needs-owner-oauth",
    owner: "owner_account",
    nextAction: "Connect Slack for AI questions, unfurls, dashboard subscriptions, and workflow notifications.",
  },
  {
    surface: "PostHog AI / AI settings",
    status: "partial-live",
    owner: "owner_account",
    nextAction: "Record org AI setting and use AI features only where the account setting allows it.",
  },
  {
    surface: "PostHog MCP / PostHog Code",
    status: "needs-owner-oauth",
    owner: "owner_account",
    nextAction: "Connect only with scoped access and a repo/project decision.",
  },
  {
    surface: "AI observability",
    status: "configured-repo",
    owner: "repo",
    nextAction: "POSTHOG_AI_OBSERVABILITY_POLICY, lib/posthog/ai-observability.ts, and npm run qa:posthog-ai-observability define the no-secret sanitized observation guard. Live export remains gated on owner approval and a server/worker project token.",
  },
  {
    surface: "AI observability guard",
    status: "configured-repo",
    owner: "repo",
    nextAction: "Use buildPostHogAiObservation before any LLM telemetry export. Raw prompts, completions, provider keys, person/replay/session data, provider OAuth, paid changes, workflow activation, and outbound customer messages stay blocked.",
  },
  {
    surface: "Evaluations and prompt management",
    status: "configured-repo",
    owner: "repo",
    nextAction: "POSTHOG_PROMPT_EVALUATION_POLICY, lib/posthog/prompt-evaluation.ts, and npm run qa:posthog-prompt-evaluation define the no-secret sanitized prompt-evaluation guard. Live export and prompt management remain gated on prompt/rubric ownership, owner approval, and a server/worker project token.",
  },
  {
    surface: "Prompt evaluation guard",
    status: "configured-repo",
    owner: "repo",
    nextAction: "Use buildPostHogPromptEvaluation before any prompt-evaluation telemetry export. Raw prompts, completions, samples, provider keys, person/replay/session data, production prompt mutation, provider OAuth, paid changes, workflow activation, and outbound customer messages stay blocked.",
  },
  {
    surface: "Endpoints",
    status: "configured-live",
    owner: "posthog_live",
    nextAction: "Maintain all 32 active/materialized endpoint slugs and verify worker API reads after key approval.",
  },
  {
    surface: "Embedded/internal analytics",
    status: "configured-repo",
    owner: "repo",
    nextAction: "POSTHOG_EMBEDDED_ANALYTICS_POLICY, lib/posthog/embedded-analytics.ts, and npm run qa:posthog-embedded-analytics define the no-secret endpoint-row sanitizer for visitor map, CRM dashboards, portal aggregates, and worker health APIs. Live endpoint queries remain gated on owner approval and a server/worker API key.",
  },
  {
    surface: "Embedded analytics guard",
    status: "configured-repo",
    owner: "repo",
    nextAction: "Use sanitizePostHogEmbeddedAnalyticsRows and buildPostHogEmbeddedAnalyticsSnapshot before embedding endpoint data. Unknown endpoints, raw person/contact/replay/session data, browser API-key exposure, provider OAuth, paid changes, and outbound customer messages stay blocked.",
  },
  {
    surface: "Alerts and subscriptions",
    status: "partial-live",
    owner: "posthog_live",
    nextAction: "Browser exception events now have an active Browser Web Push destination. POSTHOG_ALERT_ROUTING_POLICIES defines ten no-secret assignment/suppression routes, and POSTHOG_WORKFLOW_DRAFTS defines seven no-secret workflow drafts for internal triage. Live workflow activation and Telegram/Slack/email/GitHub/Discord destinations remain owner-gated.",
  },
  {
    surface: "Alert routing policy",
    status: "configured-repo",
    owner: "repo",
    nextAction: "Use docs/posthog-alert-routing-policy.md and npm run qa:posthog-alert-routing before creating any PostHog Workflow, destination, dashboard subscription, or escalation rule. Existing exception push remains the only live alert destination.",
  },
  {
    surface: "Source-map upload workflow",
    status: "configured-live",
    owner: "repo",
    nextAction: "Vercel Production stores the guarded PostHog CLI env from key label SEOH Vercel source maps 20260604; deployment BUAP8Gix4 uploaded 20 symbol sets, deployment HLaBnyiB3 uploaded 22 symbol sets for commit c2d10b3, public chunk .map URLs return 404, and the repo wrapper now defaults live uploads to --delete-after unless an explicit keep-after-upload opt-out is set.",
  },
  {
    surface: "Release/deploy tracking",
    status: "configured-repo",
    owner: "repo",
    nextAction: "Keep release properties wired into browser/server events. POSTHOG_DEPLOY_MARKER_POLICY, lib/posthog/deploy-marker.ts, and npm run qa:posthog-deploy-marker define the no-secret deploy marker guard; live marker sends require release-owner approval and both deploy marker env gates.",
  },
  {
    surface: "Deploy marker guard",
    status: "configured-repo",
    owner: "repo",
    nextAction: "Use npm run posthog:deploy-marker:dry-run before enabling any live marker runner. Do not set POSTHOG_DEPLOY_MARKER_LIVE_SEND_APPROVED=true until release ownership is assigned.",
  },
  {
    surface: "Log processor guard",
    status: "configured-repo",
    owner: "repo",
    nextAction: "Use buildPostHogLogRecord and npm run qa:posthog-log-processor before wiring any server or worker log exporter. Live ingestion is blocked until endpoint/header storage and volume limits are approved.",
  },
  {
    surface: "Data quality QA",
    status: "configured-repo",
    owner: "repo",
    nextAction: "Run qa:posthog-setup with analytics and piworker QA before marking PostHog setup changes ready.",
  },
] as const satisfies readonly PostHogSetupSurface[];
